<template>
  <div>
    <!-- Success and Error Message Slot -->
    <div v-if="showMessage" :class="['message', messageType]" class="message-container">
      <slot name="message">{{ message }}</slot>
    </div>
    <div class="grid-container">
      <!-- Column 1: Template, Date Range, Media, and Interval -->
      <div
        class="grid-item"
        :class="{ disabled: isApiCallsLoading || advanceFilterLoading || !isPageReady }"
      >
        <div class="section">
          <div class="filter">
            <div class="filter-ttl"><span>TEMPLATE</span></div>
            <div class="filter-group-report">
              <div class="flp-controls">
                <b-loading-spinner2 v-if="isApiCallsLoading" :width="20" />
                <b-single-select
                  v-else
                  id="templates"
                  class="b-select"
                  :options="templateOptions"
                  :selected="filters.template"
                  :capitalize-text="false"
                  :disabled="isAdvanceFilterLoading"
                  @change="onSelectTemplate"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="section">
          <div class="filter">
            <div class="filter-ttl"><span>DATE RANGE</span></div>
            <div class="filter-group-report">
              <div class="pan-content-top pan1-content-top">
                <b-datepicker
                  :key="dateKey"
                  :week1="[dates.startDate, dates.endDate]"
                  :campaign-options="isMniAccount ? campaignOptions : ioOptions"
                  :active-solution-id="activeSolutionId"
                  :hide-compare-with="true"
                  :on-reports-builder="true"
                  @change="bDatepickerChanges"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="section">
          <div class="filter media-settings">
            <div class="filter-ttl">
              <span>MEDIA</span>
            </div>
            <div class="filter-group-report mediaFilters">
              <div
                ref="campaignList"
                class="campaign-opts"
                :class="{ 'error-highlight': showErrorOnCampaign }"
              >
                <dropdown-checkbox-search
                  key="campaign"
                  name="campaign"
                  label="Search..."
                  :options="isMniAccount ? filters.campaignOptions : ioOptions"
                  :fancy="true"
                  :multiple="true"
                  :campaign-options-changed="campaignOptionsChanged"
                  label-key="value"
                  title=""
                  header="Campaigns"
                  component-id="ioOptionDropdown"
                  :allow-multi-select="true"
                  :show-all="false"
                  :value="
                    isMniAccount
                      ? selectedFilters.campaignOptions
                        ? selectedFilters.campaignOptions
                        : []
                      : selectedFilters.ioOptions
                      ? selectedFilters.ioOptions
                      : []
                  "
                  original-component="dropdown"
                  @input="
                    (val) => setSelectedFilters(isMniAccount ? 'campaignOptions' : 'ioOptions', val)
                  "
                  @fetch-ad-options-from-campaigns="fetchAdOptionsFromCampaigns(null)"
                  @dropdown-closed="dropdownIOClosed"
                />
              </div>
              <dropdown-checkbox-search
                key="adGroup"
                name="adGroup"
                label="Search..."
                :options="adGroupOptions || filters.adGroupOptions"
                :fancy="true"
                :multiple="true"
                :loading="isAdvanceFilterLoading && !isMniAccount && campaignOptionsChanged"
                :campaign-options-changed="campaignOptionsChanged"
                label-key="value"
                title=""
                header="Ad Group"
                class="media-select"
                component-id="adGroupDropdowns"
                :allow-multi-select="true"
                :show-all="false"
                :value="
                  selectedFilters && selectedFilters.adGroupOptions
                    ? selectedFilters.adGroupOptions
                    : []
                "
                original-component="dropdown"
                :class-name="
                  `advance-filter-hover-adgroup ${isAdvanceFilterLoading ? 'loading-dropdown' : ''}`
                "
                :disabled="isMniAccount || isAdGroupLoading || isAdvanceFilterLoading"
                @input="(val) => setSelectedFiltersAdG('adGroupOptions', val)"
                @selected-options="(val) => setSelectedFiltersAdG('adGroupOptions', val, true)"
                @dropdown-closed="dropdownAdGrpClosed"
              />
              <div
                class="fp-text-link view-pop-fltr"
                :class="{
                  disabled:
                    isAdGroupLoading ||
                    isAdvanceFilterLoading ||
                    advanceFilterLoading ||
                    disabledAdvanceFilter,
                }"
              >
                <p @click="toggleAdvanceFilterModal">Advanced Filters</p>
                <span
                  >{{ appliedFilterCount > 0 ? `(${appliedFilterCount} Applied)` : '' }}
                  <font-awesome-icon
                    v-if="!isAdGroupLoading"
                    :icon="['far', 'times']"
                    class="advance-filter-clear-icon"
                    @click="onClearAdvanceFilters"
                  />
                </span>
              </div>
              <div
                class="fp-text-link reset-to-default-link"
                :class="{
                  disabled: isAdGroupLoading || isAdvanceFilterLoading || advanceFilterLoading,
                }"
              >
                <p @click="onResetToDefault">Reset to Default</p>
              </div>
            </div>
          </div>
        </div>
        <div class="section">
          <div class="filter aggregation">
            <div class="filter-ttl">
              <span>INTERVAL</span>
              <span v-if="singleSelectModeDimension">
                <font-awesome-icon
                  :icon="['fas', 'info-circle']"
                  class="tooltip-icon"
                  @mouseenter="showIntervalTooltip = true"
                  @mouseleave="showIntervalTooltip = false"
                />
              </span>
            </div>
            <div
              class="filter-group-report"
              :class="{ disabled: disableInterval || singleSelectModeDimension }"
            >
              <b-select
                id="interval"
                class="aggregation-type"
                :options="filters.intervalOptions"
                :selected="filters.interval"
                @change="onSelectInterval"
              />
            </div>
          </div>
        </div>
        <div class="filter method-settings">
          <div v-if="!isMniAccount">
            <div class="filter-ttl"><span>ATTRIBUTION SETTINGS</span></div>
            <div class="filter-group-report" :class="`${isVCRCategory ? 'readonly-db' : ''}`">
              <b-radio-tab-group
                :initial-group="filters.totalOrUniqueRadioOptions || totalOrUniqueRadioOptions"
                :is-mni-account="isMniAccount"
                class="tot-uniq"
                :disable-unique="disableUniqueAttribution"
                @update="updateTotalOrUniqueRadioOptions"
              />
            </div>
            <div class="filter-ttl"><span>Methodology</span></div>
            <div class="filter-group-report" :class="`${isVCRCategory ? 'readonly-db' : ''}`">
              <b-select
                class="media-select"
                :options="filters.methodologyOptions"
                :selected="filters.methodology"
                :disabled="isMniAccount"
                @change="onSelectMethodology"
              />
            </div>
          </div>
          <div class="filter-ttl">
            <span>{{ isMniAccount ? 'Lookback Window' : 'Conversion Window' }}</span>
          </div>
          <div
            class="conv-content filter-group-report"
            :class="{
              'readonly-db': isVCRCategory,
              disabled: disableLookBackWindow,
            }"
          >
            <div class="conversion-opt-cont">
              <div class="opt-b1" data-children-count="1">
                <input
                  id="conversion_window_value"
                  v-model="filters.conversionWindowDisplay"
                  type="text"
                  name="conversion-window"
                  class="flp-input input-conv"
                  @keypress="onlyNumber"
                  @input="validateConversionWindow"
                  @blur="handleBlur"
                  @paste="handlePaste"
                />
              </div>
              <div class="opt-b2">
                <b-checkbox
                  id="days_conversion_window"
                  label="Days"
                  name="Days"
                  icon="circle"
                  :is-radio="true"
                  :checked="filters.conversionWindowUnit === 'DAYS'"
                  @change="updateConversionWindowUnit"
                />
                <b-checkbox
                  id="hours_conversion_window"
                  label="Hours"
                  name="Hours"
                  icon="circle"
                  :is-radio="true"
                  :checked="filters.conversionWindowUnit === 'HOURS'"
                  @change="updateConversionWindowUnit"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Column 2: Attribution Settings, Methodology, Conversion Window -->
      <div class="grid-item" :class="{ disabled: !isPageReady }">
        <div class="section">
          <div class="filter checkbox-select" :class="{ 'error-highlight': showErrorOnDimension }">
            <div ref="dimensionsList" class="filter-ttl">
              <span>DIMENSIONS</span>
              <span v-if="singleSelectModeDimension">
                <font-awesome-icon
                  :icon="['fas', 'info-circle']"
                  class="tooltip-icon"
                  @mouseenter="showTooltip = true"
                  @mouseleave="showTooltip = false"
                />
              </span>
            </div>
            <!-- Dynamic Options -->
            <div
              v-for="option in reportDimensionsOptions"
              :key="option.id"
              :class="{
                disabled: disableAllDimensions,
              }"
              class="checkbox-select__check-wrapp dimension-item"
            >
              <input
                :id="option.id"
                v-model="selectedDimensionsOptions"
                class="conditions-check"
                type="checkbox"
                :value="option.id"
                :disabled="disableAllDimensions"
                @change="handleDimensionChange(option.id)"
              />
              <label :for="option.id">{{ option.name }}</label>
            </div>
          </div>
        </div>
      </div>
      <!-- Column 3: Dimensions -->
      <div class="grid-item metrics-opts" :class="{ disabled: !isPageReady }">
        <div class="section">
          <div class="metrics-label"><span>EVENTS</span></div>
          <b-loading-spinner2 v-if="isEventsLoading || isApiCallsLoading" :width="20" />
          <div v-else class="filter checkbox-select">
            <div
              v-for="(event, index) in eventOptions"
              :key="`event-${index}`"
              :class="{
                disabled: isEventDisabled(event),
              }"
              class="checkbox-select__check-wrapp dimension-item"
            >
              <!-- Main Event Checkbox -->
              <input
                :id="`event-${index}`"
                v-model="event.selected"
                type="checkbox"
                class="conditions-check"
                :value="event.category"
                :disabled="isEventDisabled(event)"
                :readonly="singleSelectMode && event.selected"
                @change="toggleMainCategory(index)"
              />
              <label :for="`event-${index}`">
                {{
                  event.event === 'Video Completions'
                    ? 'Completions'
                    : isMniAccount && event.event === 'Checkouts'
                    ? 'Conversions'
                    : event.event
                }}
                <span v-if="event.numSubCategories && event.children.length > 0"
                  >({{ event.numSubCategories }} Subcategories)</span
                >
                <span v-if="event.isSubCategoriesCountOverMaxAllowed">
                  <font-awesome-icon
                    :icon="['fas', 'info-circle']"
                    class="tooltip-icon"
                    @mouseenter="showSubCatTooltip = true"
                    @mouseleave="showSubCatTooltip = false"
                  />
                </span>
              </label>

              <!-- Subcategories -->
              <div
                v-if="event.selected && event.children && event.children.length"
                class="subcategory-container"
              >
                <div
                  v-for="(subCategory, subIndex) in event.children"
                  :key="`event-${index}-subCategory-${subIndex}`"
                  class="checkbox-select__check-wrapp dimension-item subcategory-item tooltip"
                >
                  <span v-if="subCategory.event === 'Blank'" class="tooltiptext"
                    >No subcategory assigned (null/none).</span
                  >
                  <input
                    :id="`event-${index}-subCategory-${subIndex}`"
                    v-model="subCategory.selected"
                    type="checkbox"
                    class="conditions-check"
                    :value="subCategory.category"
                    @change="resetSubCategories"
                  />
                  <label :for="`event-${index}-subCategory-${subIndex}`">
                    {{ subCategory.event }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Column 4: Metrics -->
      <div class="grid-item" :class="{ disabled: !isPageReady }">
        <div class="section">
          <div class="metrics-label"><span>METRICS</span></div>
          <div class="filter checkbox-select">
            <!-- SELECT ALL Option -->
            <div
              class="checkbox-select__check-wrapp dimension-item"
              :class="{ disabled: disabledAllMetrics }"
            >
              <input
                id="select-all-metrics"
                v-model="selectAllMetrics"
                type="checkbox"
                class="conditions-check"
                :disabled="disabledAllMetrics"
                @change="toggleSelectAllMetrics"
              />
              <label for="select-all-metrics">SELECT ALL</label>
            </div>
            <!-- Dynamic Options -->
            <div
              v-for="option in reportMetricsOptions"
              :key="option.key"
              :class="{ disabled: disabledAllMetrics }"
              class="checkbox-select__check-wrapp dimension-item"
            >
              <input
                :id="option.key"
                v-model="selectedMetricsOptions"
                type="checkbox"
                class="conditions-check"
                :value="option.key"
                :disabled="disabledAllMetrics"
                @change="handleMetricsChange(option.key)"
              />
              <label :for="option.key">{{ getMetricLabel(option) }}</label>
            </div>
          </div>
        </div>
      </div>
      <!-- Column 5: Delivery -->
      <div :class="{ disabled: !isPageReady }">
        <DeliveryGrid
          :run-now="runNow"
          :save-template="saveTemplate"
          :schedule-report="scheduleReport"
          :export-n-send-report="exportNSendReport"
          :report-name="reportName"
          :frequency-val="selectedFrequency"
          :sched-start-date="scheduleStartDate"
          :sched-end-date="scheduleEndDate"
          :email-to-val="emailsToSched"
          :email-to-send="emailsToSend"
          :is-valid-report-name="isValidReportName"
          :button-label="buttonLabel"
          :disable-save-report-template="disableSaveReportTemplate"
          :disabled-run-btn="disabledRunBtn || isAdvanceFilterLoading || advanceFilterLoading"
          :disable-save-report-based-on-sched-report="disableSaveReportBasedOnSchedReport"
          :show-error-on-template-name="showErrorOnTemplateName"
          :show-error-on-email-to="showErrorOnEmailTo"
          :show-error-on-schedule="showErrorOnSchedule"
          :show-error-on-delivery-vals="showErrorOnDeliveryVals"
          :show-error-on-run-now-opts="showErrorOnRunNowOpts"
          :disable-schedule-report="disableScheduleReport"
          :disable-run-now-selection="disableRunNowSelection"
          @run-now-changed="handleRunNowChange"
          @save-template-changed="handleSaveTemplateChange"
          @report-name-changed="trimReportName"
          @run-or-save-report="runOrSaveReport"
          @export-n-send-report-changed="handleExportNSendReportChange"
          @schedule-report-changed="handleScheduleReportChange"
          @on-frequency-change="handleFrequencyChange"
          @emails-changed="handleEmailChange"
          @schedule-start-date-changed="handleScheduleStartDateChange"
          @schedule-end-date-changed="handleScheduleEndDateChange"
          @emails-to-send-changed="handleEmailToSendChange"
        />
      </div>

      <div v-if="showEventChangePopup.visible" class="show-event-change-popup">
        <EventConfirmModal
          :description="showEventChangePopup.description"
          @cancel="onCloseEventChangePopup"
          @change="onChangeEventChangePopup"
        />
      </div>

      <transition name="expand">
        <div v-if="sendingEmail" class="sending-alert">
          <span v-if="sendingEmail && !emailSent" class="alt-txt1">Generating Report...</span>
          <span v-if="emailSent" class="alt-txt2">Report sent to Email/s!</span>
          <div class="alt-loader">
            <div class="alt-loader-progress" :class="[emailSent ? 'progress100' : '']"></div>
            <div class="alt-loader-bg"></div>
          </div>
        </div>
      </transition>
    </div>
    <div v-if="showTooltip" class="custom-dimension-tooltip">
      {{ infoText }}
    </div>
    <div v-if="showIntervalTooltip" class="custom-interval-tooltip">
      {{ infoIntervalText }}
    </div>
    <div v-if="showSubCatTooltip" class="custom-subcat-tooltip">
      {{ subCatInfoText }}
    </div>
  </div>
</template>

<script>
import { get, sync } from 'vuex-pathify';
import planApi from 'adready-api/api/plans';
import fetchIosMethodMixin from '@/components/mixins/fetch-ios-method-mixin';
import advanceFilterApiMixin from '@/components/mixins/advance-filter-api-mixin';
import fetchCampaignsMethodMixin from '@/components/mixins/fetch-campaigns-method-mixin';
import fetchEventsMethodMixin from '@/components/mixins/fetch-events-method-mixin';
import gaEventsMixin from '@/components/mixins/ga-events-mixin';
import BDatepicker from '@/components/elements/b-datepicker.vue';
import BSelect from '@/components/elements/b-select.vue';
import BRadioTabGroup from '@/components/elements/b-radio-tab-group.vue';
import BCheckbox from '@/components/elements/b-checkbox.vue';
import DropdownCheckboxSearch from '@/components/elements/b-auto-complete-dropdown3.vue';
import BSingleSelect from '@/components/elements/b-single-select.vue';

import {
  ACCOUNTS_TO_SHOW,
  MNI_ACCOUNT_ID,
  MNI_DEMO_ACCOUNT_ID,
  REPORT_BUILDER_DISABLED_METRICS,
  REPORTS_NEW_BUILDER_METRIC_OPTIONS,
  NEW_REPORT_OPTIONS,
  ERROR_MESSAGE_FETCH_TEMPLATE,
  ERROR_MESSAGE_DELIVERY_SELECTION,
  ERROR_MESSAGE_NAME_REQUIRED,
  DEFAULT_METHODOLOGY,
  DEFAULT_CONVERSION_WINDOW_UNIT,
  DEFAULT_CONVERSION_WINDOW_DISPLAY,
  MESSAGES,
  COST_METRICS_KEYS_TO_HIDE,
  NEW_REPORT,
  PRE_DEFINED_TEMPLATES,
  PRE_DEFINED_TEMPLATES_WITH_NEW,
  FOOT_TRAFFIC_ATTRIBUTED_VISITS_REPORT,
  FOOT_TRAFFIC_SUMMARY_REPORT,
  ALL_TOUCH_REPORT,
  LAST_TOUCH_REPORT,
  LOC_VISIT_EVENT,
  FOOT_TRAFFIC_ATTRIBUTED_VISITS_REPORT_TEMPLATE,
  FOOT_TRAFFIC_SUMMARY_REPORT_TEMPLATE,
  UNCATEGORIZED_EVENT_CATEGORY,
  AUDIENCE_INSIGHTS_REPORT_TEMPLATE,
  PRE_DEFINED_TEMPLATES_AUDIENCE_INSIGHTS,
  AUDIENCE_INSIGHTS_CONVERSION_REPORT,
  AUDIENCE_INSIGHTS_CAMPAIGNS_REPORT,
  MEDIATYPE_FILTER,
  AUDIENCE_FILTER,
  PRE_DEFINED_TEMPLATES_WITH_NO_AUDIENCE_INSIGHTS,
  INSIGHTS_CMPGN_DELIV_PRESET_TEMPLATE_NAME,
  INSIGHTS_CONV_EVNT_PRESET_TEMPLATE_NAME,
  CAT_SUBCAT_REPORT,
  CAT_SUBCAT_REPORT_TEMPLATE_NAME,
  AUDIENCE_INSIGHTS_REPORT_TEMPLATE_EXCLUDED_ACCOUNTS,
  ERROR_MESSAGE_SCHEDULED_PRESET_REPORT,
  MAX_RUN_REPORT_COUNT_REACH_ERROR_MSG,
  RUN_REPORT_TIMESTAMPS_KEY,
  FIRST_DAG_SUBMISSION_FAILURE_MESSAGE,
  PARTIAL_SUCCESS,
  MAX_SUB_CAT_INFO_TEXT,
  FOOT_TRAFFIC_SUMMARY_REPORT_TEMPLATE_NAME,
  KEY_DEMO_ADVERTISER_ID,
  RANGE_LAST_30_DAYS,
  RANGE_CUSTOM,
} from '@/constant';
import EventBus from '@/adready-vue/helpers/global/event-bus';
import {
  convertEpochToNYTimezone,
  generateFlipDefaultDates,
  getCampaignDateRange,
  offsetDate,
  verifyRunCount,
  isDemoInstance,
} from '@/util/utility-functions';
import appConsoleDataLayer from '@/api/app-console-data';
import bLoadingSpinner2 from '@/components/elements/b-loading-spinner2.vue';
import { formatDateForAPI } from '@/util/apiDateFormat';
import { getCachedFilters } from '@/helpers/global/url-helpers';
import ioHelpers from '@/helpers/io-helpers';
import { isBlank } from 'adready-api/helpers/common';
import { isEqual } from 'underscore';
import moment from 'moment';
import reportBuilderV2API from '@/api/report-builder-v2-api';
import * as util from '~/util/utility-functions';
import DeliveryGrid from './delivery-grid.vue';
import config from '~/config';
import EventConfirmModal from '../EventConfirmModal.vue';

export default {
  name: 'ReportBuilder',
  components: {
    BDatepicker,
    BSelect,
    BRadioTabGroup,
    BCheckbox,
    DropdownCheckboxSearch,
    BSingleSelect,
    DeliveryGrid,
    bLoadingSpinner2,
    EventConfirmModal,
  },
  mixins: [
    fetchIosMethodMixin,
    fetchCampaignsMethodMixin,
    fetchEventsMethodMixin,
    gaEventsMixin,
    advanceFilterApiMixin,
  ],
  props: {
    activeSolutionId: {
      type: Number,
      required: false,
      default: 8, // mni
    },
    touchReportsEmailStatus: {
      type: Object,
      required: false,
      default: () => ({
        done: false,
        status: '',
        emails: '',
      }),
    },
  },
  data() {
    const filterOptionsArray = [
      'audienceOptions',
      'creativeOptions',
      'adGroupOptions',
      'mediaTypeOptions',
      'campaignOptions',
      'ioOptions',
      'keywordOptions',
      'publisherOptions',
    ];
    return {
      config,
      filters: this.genFilters(),
      initialFilters: {},
      isAdGroupLoading: false,
      reportDimensionsOptions: [],
      selectedDimensionsOptions: ['creative'],
      disableAllDimensions: false,
      reportMetricsOptions: [],
      selectedMetricsOptions: [],
      disabledAllMetrics: false,
      selectAllMetrics: false,
      disabledAllEvents: false,
      singleSelectMode: false,
      singleSelectModeDimension: false,
      selectedReportType: 'performance',
      selectedReportKind: 'creative',
      allEventPerformance: false,
      runNow: true, // default
      saveTemplate: false,
      scheduleReport: false,
      disableSaveReportBasedOnSchedReport: false,
      exportNSendReport: false,
      selectedFrequency: 'select', // DEFAULT
      emailsToSched: [],
      emailsToSend: [],
      scheduleStartDate: '',
      scheduleEndDate: '',
      reportName: '',
      buttonLabel: 'RUN REPORT',
      showTooltip: false,
      showSubCatTooltip: false,
      showIntervalTooltip: false,
      disableSaveReportTemplate: false,
      disableScheduleReport: false,
      exportResults: false,
      reportFrequency: 'Monthly',
      emailRecipients: '',
      campaignOptionsChanged: false,
      selectedFilters: this.getSelectedFilters(this.genFilters(), filterOptionsArray),
      dateKey: 0,
      isAllOrLastTouch: false,
      isInitialCampaignChange: true,
      isInitialAdGChange: true,
      isInitialFilterChange: true,
      isInitialConversionWindowChange: true,
      isInitialConversionUnitChange: true,
      isApiCallsLoading: false,
      startTime: null,
      retryTimeout: 20000,
      showMessage: false,
      message: '',
      messageType: 'success',
      selectedTemplateId: '',
      infoText:
        'Only single select is allowed when All Events and Performance is selected as metrics.',
      subCatInfoText: MAX_SUB_CAT_INFO_TEXT,
      disableInterval: false,
      infoIntervalText:
        'Hourly and Daily interval are added as Dimension when All Events and Performance is selected as metrics.',
      sendingEmail: false,
      emailSent: false,
      disabledRunBtn: false,
      selectedTemplateType: '',
      selectedTemplateOption: 'new',

      showErrorOnDimension: false,
      showErrorOnCampaign: false,
      showErrorOnTemplateName: false,
      showErrorOnEmailTo: false,
      showErrorOnSchedule: false,
      showErrorOnDeliveryVals: false,
      showErrorOnRunNowOpts: false,
      advanceFilterLoading: false,

      selectedCampaignGoal: {},
      isAdvanceFilterLoading: false,

      showEventChangePopup: { visible: false, payload: {} },
      isEventsLoading: false,
      adGroupsLoaded: false,
      triggerFilterChangeOnCampaign: true,
      disableLookBackWindow: false,
      disabledAdvanceFilter: false,
      initialFilterChange: true,
      isRestoringSession: false,
      isPageReady: false,
    };
  },
  computed: {
    account: get('common/account'),
    demoAdvertisers: get('common/GET_DEMO_ADVERTISER_MAPPING_MAP'),
    dates: get('dashboard/dates'),
    isMniAccount: get('common/isMniAccount'),
    mniTemplateId: get('common/mniTemplateId'),
    isViaAccount: get('common/isViaAccount'),
    eventOptions: get('dashboard/filters@eventOptions'),
    adGroupOptions: get('dashboard/filters@adGroupOptions'),
    publisherOptions: get('dashboard/filters@publisherOptions'),
    pixelOptions: get('dashboard/filters@pixelOptions'),
    mediaTypeOptions: get('dashboard/filters@mediaTypeOptions'),
    keywordOptions: get('dashboard/filters@keywordOptions'),
    audienceOptions: get('dashboard/filters@audienceOptions'),
    creativeOptions: get('dashboard/filters@creativeOptions'),
    conversionWindow: get('dashboard/filters@conversionWindow'),
    advertiser: get('common/advertiser'),
    totalOrUniqueRadioOptions: get('dashboard/filters@totalOrUniqueRadioOptions'),
    ioOptions: get('dashboard/filters@ioOptions'),
    campaignOptions: get('dashboard/filters@campaignOptions'),
    universalPixelId: get('common/universalPixelId'),
    selectedExportTableRow: sync('common/selectedExportTableRow'),
    selectedEvent: get('dashboard/GET_SELECTED_ALL_EVENTS'),
    defaultEventSelected: get('dashboard/filters@defaultEventSelected'),
    selectedMediaTypes: get('dashboard/GET_SELECTED_MEDIATYPES'),
    selectedPublishers: get('dashboard/GET_SELECTED_PUBLISHERS'),
    selectedCreatives: get('dashboard/GET_SELECTED_CREATIVES'),
    selectedAudiences: get('dashboard/GET_SELECTED_AUDIENCES'),
    templatesList: get('dashboard/templates@data'),
    selectedAdGrpOptStr: get('dashboard/GET_SELECTED_AD_GROUP_STR'),
    advertiserId() {
      return this.advertiser ? this.advertiser.id : 0;
    },
    hideCostMetrics() {
      return this.advertiser ? this.advertiser.hideCostMetrics : false;
    },
    isValidReportName() {
      return this.reportName.trim().length > 0;
    },
    isSaveButtonDisabled() {
      return false;
    },
    isVCRCategory() {
      return (
        !this.isMniAccount &&
        this.selectedEvent.length !== 0 &&
        this.selectedEvent.every((event) => {
          if (!event) return false;
          return event.isFalseEvent === true;
        })
      );
    },
    isCreativeOnly() {
      return (
        this.selectedDimensionsOptions.length === 1 &&
        this.selectedDimensionsOptions.includes('creative')
      );
    },
    hasOnlySingleEventSelected() {
      return false; // Placeholder for future logic
    },
    appliedFilterCount: get('common/appliedFilterCount'),
    appliedFilterCountNew() {
      let appliedCount = 0;
      const filterUsed = this.filters;

      const filterKeys = [
        'campaignOptions',
        'ioOptions',
        'adGroupOptions',
        'mediaTypeOptions',
        'audienceOptions',
        'creativeOptions',
        'publisherOptions',
      ];

      filterKeys.forEach((key) => {
        if (filterUsed[key]?.some((v) => v.checked)) {
          appliedCount += 1;
        }
      });
      return appliedCount;
    },
    templateOptions() {
      const data = this.$store.copy('dashboard/templates@data');
      const dynamicOptions = this.mapAndSortOptions(data);
      let defaultTemplate = this.$store.copy('dashboard/filters@templateOptions');
      defaultTemplate = this.mapAndSortOptions(defaultTemplate);
      if (!this.isMniAccount) {
        const idsToRemove = [
          AUDIENCE_INSIGHTS_CAMPAIGNS_REPORT,
          AUDIENCE_INSIGHTS_CONVERSION_REPORT,
        ];
        defaultTemplate.filter((template) => {
          return !idsToRemove.includes(template.id);
        });
      }
      this.$store.set('dashboard/filters@templateOptions', defaultTemplate);
      let templateOptions = [...defaultTemplate, ...dynamicOptions];
      templateOptions = this.mapAndSortOptions(templateOptions);
      return templateOptions;
    },
    isDemoTouchReport() {
      return (
        isDemoInstance() &&
        [ALL_TOUCH_REPORT, LAST_TOUCH_REPORT].includes(
          this.selectedTemplateOption || this.selectedTemplateType || this.filters.template
        )
      );
    },
    disableRunNowSelection() {
      let disableRunNow = false;
      if (
        PRE_DEFINED_TEMPLATES.includes(this.filters.template) ||
        PRE_DEFINED_TEMPLATES.includes(this.selectedTemplateType)
      ) {
        disableRunNow = true;
      }
      if (disableRunNow && this.isDemoTouchReport) {
        disableRunNow = false;
      }
      return disableRunNow;
    },
    disableUniqueAttribution() {
      return this.selectedEvent.some((item) => item.isRevenueEvent);
    },
  },
  watch: {
    appliedFilterCountNew: {
      immediate: true,
      handler(val) {
        this.$store.dispatch('common/setAppliedFilterCount', val);
      },
    },
    campaignOptions: {
      deep: true,
      immediate: true,
      handler(n, o) {
        if (isEqual(n, o)) return;
        if (n) {
          this.filters.campaignOptions = JSON.parse(JSON.stringify(n));
          this.selectedFilters.campaignOptions = this.filters.campaignOptions.filter(
            (item) => item.checked
          );
        }
      },
    },
    ioOptions: {
      deep: true,
      immediate: true,
      handler(n, o) {
        if (isEqual(n, o)) {
          return;
        }
        if (this.isMniAccount) {
          return;
        }
        this.filters.ioOptions = JSON.parse(JSON.stringify(n));
        this.selectedFilters.ioOptions = this.filters.ioOptions.filter((item) => item.checked);
      },
    },
    adGroupOptions: {
      deep: true,
      immediate: true,
      handler(n, o) {
        if (isEqual(n, o)) {
          return;
        }
        if (this.isMniAccount) {
          return;
        }
        this.filters.adGroupOptions = JSON.parse(JSON.stringify(n));
        this.selectedFilters.adGroupOptions = this.filters.adGroupOptions.filter(
          (item) => item.checked
        );
      },
    },
    mediaTypeOptions: {
      deep: true,
      immediate: true,
      handler(n, o) {
        if (!this.isMniAccount) {
          if (util.deepEqual(n, o)) {
            return;
          }
          if (n) {
            this.filters.mediaTypeOptions = JSON.parse(JSON.stringify(n));
          }
        } else if (n) {
          this.filters.mediaTypeOptions = JSON.parse(JSON.stringify(n));
        }
      },
    },
    touchReportsEmailStatus: {
      deep: true,
      immediate: true,
      handler(n) {
        if (n.done) {
          try {
            setTimeout(() => {
              this.disabledRunBtn = false;
              this.sendingEmail = false;
            }, 2000);

            if (n.status === 'success') {
              this.showSuccessMessage(MESSAGES.REPORT_EMAIL_SUCCESS(n.emails));
              this.emailSent = true;
            } else if (n.status === PARTIAL_SUCCESS) {
              this.showSuccessMessage(FIRST_DAG_SUBMISSION_FAILURE_MESSAGE);
              this.emailSent = true;
            } else {
              this.emailSent = false;
              this.showErrorMessage(n.status);
            }
          } finally {
            setTimeout(() => {
              this.disabledRunBtn = false;
              this.sendingEmail = false;
              this.emailSent = false;
            }, 2000);
          }
        }
      },
    },
    eventOptions: {
      deep: true,
      immediate: true,
      handler(n, o) {
        if (util.deepEqual(n, o)) {
          return;
        }
        if (n) {
          if (this.hideCostMetrics) {
            // hide from the list
            this.$store.dispatch('dashboard/filterEventOptions', COST_METRICS_KEYS_TO_HIDE);
          }
          if (
            this.eventOptions.some(
              (event) => event.event === LOC_VISIT_EVENT && event.category === LOC_VISIT_EVENT
            )
          ) {
            this.$store.dispatch(
              'dashboard/addPredefinedTemplateToOptions',
              FOOT_TRAFFIC_ATTRIBUTED_VISITS_REPORT_TEMPLATE
            );
            this.$store.dispatch(
              'dashboard/addPredefinedTemplateToOptions',
              FOOT_TRAFFIC_SUMMARY_REPORT_TEMPLATE
            );
          } else {
            this.$store.dispatch(
              'dashboard/removePredefinedTemplateToOptions',
              FOOT_TRAFFIC_ATTRIBUTED_VISITS_REPORT
            );
            this.$store.dispatch(
              'dashboard/removePredefinedTemplateToOptions',
              FOOT_TRAFFIC_SUMMARY_REPORT
            );
          }

          this.filters.eventOptions = JSON.parse(JSON.stringify(n));
          this.selectAllMetrics = false;
          this.setMetricOptsForEvents(this.reportMetricsOptions);
          this.$store.dispatch('dashboard/sortEventOptions');
        }
      },
    },
    audienceOptions: {
      deep: true,
      immediate: true,
      handler(n, o) {
        if (!this.isMniAccount) {
          if (util.deepEqual(n, o)) {
            return;
          }
          if (n) {
            this.filters.audienceOptions = JSON.parse(JSON.stringify(n));
          }
        } else if (n) {
          this.filters.audienceOptions = JSON.parse(JSON.stringify(n));
        }
      },
    },
    publisherOptions: {
      deep: true,
      immediate: true,
      handler(n, o) {
        if (util.deepEqual(n, o)) {
          return;
        }
        if (this.isMniAccount) {
          return;
        }
        if (n) {
          this.filters.publisherOptions = JSON.parse(JSON.stringify(n));
        }
      },
    },
    creativeOptions: {
      deep: true,
      immediate: true,
      handler(n, o) {
        if (util.deepEqual(n, o)) {
          return;
        }
        if (this.isMniAccount) {
          return;
        }
        if (n) {
          this.filters.creativeOptions = JSON.parse(JSON.stringify(n));
        }
      },
    },
    reportDimensionsOptions: {
      deep: true,
      async handler(n, o) {
        if (util.deepEqual(n, o)) return;
        if (n) {
          this.reportDimensionsOptions = n.sort((a, b) => {
            if (a.id === 'overall') return -1;
            if (b.id === 'overall') return 1;
            return a.name.localeCompare(b.name);
          });
        }
      },
    },
    reportMetricsOptions: {
      deep: true,
      immediate: true,
      async handler(n, o) {
        if (util.deepEqual(n, o)) {
          return;
        }
        if (n) {
          this.reportMetricsOptions = n.sort((a, b) => {
            if (a.value === 'All Events and Performance') return -1;
            if (b.value === 'All Events and Performance') return 1;
            return a.value.localeCompare(b.value);
          });
        }

        if (this.hideCostMetrics) {
          // hide from the list
          this.reportMetricsOptions = this.reportMetricsOptions.reduce((acc, item) => {
            // Check if the item should be hidden
            const shouldHide = COST_METRICS_KEYS_TO_HIDE.some((someKey) =>
              item.key.toLowerCase().includes(someKey)
            );

            if (shouldHide) {
              // Set checked to false for the hidden item
              item.checked = false;
            } else {
              // Only add to the accumulator if the item is not hidden
              acc.push(item);
            }

            return acc;
          }, []);
        }
      },
    },
    templateOptions: {
      deep: true,
      immediate: true,
      handler(n, o) {
        if (util.deepEqual(n, o)) {
          return;
        }
        if (n) {
          if (!AUDIENCE_INSIGHTS_REPORT_TEMPLATE_EXCLUDED_ACCOUNTS.includes(this.account?.id)) {
            AUDIENCE_INSIGHTS_REPORT_TEMPLATE.forEach((template) => {
              this.$store.dispatch('dashboard/addPredefinedTemplateToOptions', template);
            });
          } else {
            AUDIENCE_INSIGHTS_REPORT_TEMPLATE.forEach((template) => {
              this.$store.dispatch('dashboard/removePredefinedTemplateToOptions', template);
            });
          }
        }
      },
    },
  },
  async created() {
    EventBus.$on('advance-filter-on-apply', this.onAdvanceFilterApply);
    EventBus.$on('advance-filter-loading-done', this.onAdvanceFilterLoadingDone);
    this.reportDimensionsOptions = this.getReportDimensionsOptions();
    this.setMetricOpts(true);
    this.setMetricOptsForEvents(this.reportMetricsOptions);
    this.initializeMetricsSelection();

    if (!this.isMniAccount) {
      EventBus.$on('update-creative-options', this.updateCreativeOptions);
    }
  },
  beforeDestroy() {
    if (!this.isMniAccount) {
      this.setupNewTemplate();
    } else {
      this.resetAdvanceFilterOptions(this.initialFilters);
      this.resetEventSelection();
    }
    EventBus.$off('advance-filter-on-apply', this.onAdvanceFilterApply);
    EventBus.$off('advance-filter-loading-done', this.onAdvanceFilterLoadingDone);
    // delete initial storage values
    localStorage.removeItem('initialReportBuilderFilters');

    EventBus.$off('advance-filter-on-apply', this.onAdvanceFilterApply);
    if (!this.isMniAccount) {
      EventBus.$off('update-creative-options', this.updateCreativeOptions);
    }
  },
  async mounted() {
    this.handleMounted();
  },
  methods: {
    finalizePageReady() {
      const checkStates = () => {
        if (
          this.isApiCallsLoading ||
          this.isEventsLoading ||
          this.isAdGroupLoading ||
          this.isAdvanceFilterLoading ||
          this.advanceFilterLoading ||
          this.isRestoringSession
        ) {
          setTimeout(checkStates, 250);
        } else {
          this.isPageReady = true;
        }
      };
      setTimeout(checkStates, 500);
    },
    shouldShowTooltip(subEvent) {
      return subEvent === 'Blank';
    },
    getMetricLabel(option) {
      if (!this.isMniAccount) {
        return option.value; // Return default value if not MNI account
      }

      // Define the mapping for MNI account labels
      const mniLabelMap = {
        'Checkout Conversion Rate': 'Conversions Conversion Rate',
        'Checkout CPA': 'Conversions CPA',
        'Checkout Performance Multiplier': 'Conversions Performance Multiplier',
        Checkouts: 'Conversions',
        'Checkouts Time to Convert': 'Conversions Time to Convert',
        'Checkouts Total Exposures': 'Conversions Total Exposures',
      };

      // Return the mapped label if it exists, otherwise return the original value
      return mniLabelMap[option.value] || option.value;
    },
    async singleCampaignSettings(campaignId, isMounted = false) {
      let flipCampaignInfo = {};
      let source = 'ADREADY';
      if (campaignId) {
        try {
          const resPlan = await planApi.read(campaignId, {
            isLineItemsRequired: false,
          });
          flipCampaignInfo = resPlan.flipCampaignInfo || {};
          source = resPlan.source;
        } catch (err) {
          console.error('error getting plan info ->', err);
        }
      }

      this.selectedCampaignGoal = flipCampaignInfo?.goal ? flipCampaignInfo?.goal : {};
      const totalOrUniqueRadioOptions = this.$store.copy(
        'dashboard/filters@totalOrUniqueRadioOptions'
      );
      if (!flipCampaignInfo?.optimization?.impressions) {
        totalOrUniqueRadioOptions[1].active = false;
        totalOrUniqueRadioOptions[0].active = true;
      } else {
        totalOrUniqueRadioOptions.forEach((tuo) => {
          tuo.active =
            flipCampaignInfo?.optimization?.impressions?.value.toUpperCase() === tuo.text || false;
        });
      }
      this.updateTotalOrUniqueRadioOptions(totalOrUniqueRadioOptions);
      const conversionWindowObj = {
        name: flipCampaignInfo?.conversionWindow?.unit?.value || DEFAULT_CONVERSION_WINDOW_UNIT,
      };
      const methodologyOptions = this.$store.copy('dashboard/filters@methodologyOptions');
      let methodologyOption = {};

      methodologyOption = methodologyOptions.find((mo) => {
        return mo.name.toUpperCase() === flipCampaignInfo?.methodology?.value?.toUpperCase();
      });
      let methodologyOptionId = methodologyOption?.id;
      if (source && source === 'PIO') {
        this.filters.conversionWindowDisplay = flipCampaignInfo?.conversionWindow;
        const methodology = flipCampaignInfo?.methodology;
        if (methodology) {
          methodologyOptionId = ioHelpers.getMethodology(methodology.toLowerCase());
        }
      } else {
        this.filters.conversionWindowDisplay = flipCampaignInfo?.conversionWindow?.value;
      }
      if (!this.filters.conversionWindowDisplay) {
        this.filters.conversionWindowDisplay = DEFAULT_CONVERSION_WINDOW_DISPLAY;
      }

      this.updateConversionWindowUnit(conversionWindowObj);
      this.onSelectMethodology(methodologyOptionId || DEFAULT_METHODOLOGY);
      if (isMounted) {
        this.$store.set(
          'dashboard/filters@conversionWindowDisplay',
          this.filters.conversionWindowDisplay
        );
        this.$store.set(
          'dashboard/filters@conversionWindowUnit',
          conversionWindowObj.name.toUpperCase()
        );
        this.$store.set(
          'dashboard/filters@conversionWindow',
          this.filters.conversionWindowDisplay *
            (conversionWindowObj.name.toUpperCase() === 'DAYS' ? 24 : 1)
        );
        this.$store.set(
          'dashboard/filters@methodology',
          methodologyOptionId || DEFAULT_METHODOLOGY
        );
        this.$store.set(
          'dashboard/filters@showUniques',
          totalOrUniqueRadioOptions[1].active && !totalOrUniqueRadioOptions[1].disabled
        );
      }
    },
    defaultCampaignSettings(isMounted = false) {
      const totalOrUniqueOptions = this.$store
        .copy('dashboard/filters@totalOrUniqueRadioOptions')
        ?.map((attr, index) => {
          attr.active = index === 0;
          attr.disabled = false;
          return attr;
        });
      this.onSelectMethodology(DEFAULT_METHODOLOGY);
      this.updateConversionWindowUnit({
        name: DEFAULT_CONVERSION_WINDOW_UNIT,
      });
      this.filters.conversionWindowDisplay = DEFAULT_CONVERSION_WINDOW_DISPLAY;
      this.updateTotalOrUniqueRadioOptions(totalOrUniqueOptions);
      if (isMounted) {
        this.$store.set(
          'dashboard/filters@conversionWindowDisplay',
          DEFAULT_CONVERSION_WINDOW_DISPLAY
        );
        this.$store.set(
          'dashboard/filters@conversionWindowUnit',
          DEFAULT_CONVERSION_WINDOW_UNIT.toUpperCase()
        );
        this.$store.set(
          'dashboard/filters@conversionWindow',
          this.filters.conversionWindowDisplay *
            (DEFAULT_CONVERSION_WINDOW_UNIT.toUpperCase() === 'DAYS' ? 24 : 1)
        );
        this.$store.set('dashboard/filters@methodology', DEFAULT_METHODOLOGY);
        this.$store.set('dashboard/filters@showUniques', false);
      }
    },
    onCloseEventChangePopup() {
      this.showEventChangePopup = { visible: false, payload: {} };
    },
    onChangeEventChangePopup() {
      const { payload } = this.showEventChangePopup;
      const { newSelectedOptions } = payload;
      this.$store.set('dashboard/filters@eventOptions', newSelectedOptions);
      this.showEventChangePopup = { visible: false, payload: {} };

      this.$nextTick(() => {
        this.filters.eventOptions = newSelectedOptions;
      });
    },
    changeEventOptions(prevIoOptions = [], isCheckForPopup = false) {
      this.isEventsLoading = true;
      const newEventOptions = JSON.parse(JSON.stringify(this.eventOptions));
      const subCategory = this.selectedCampaignGoal?.subCategories;
      const newSelectedOptions = newEventOptions.map((ev) => {
        ev.selected = ev.category === this.selectedCampaignGoal?.category && ev.event !== 'Revenue';
        ev.open = ev.selected;
        const { children } = ev;
        children?.forEach((child) => {
          if (child.category === UNCATEGORIZED_EVENT_CATEGORY) {
            child.selected = false;
          } else {
            child.selected = subCategory?.includes(child.category);
          }
        });
        return ev;
      });
      if (newSelectedOptions.filter((sev) => !sev.selected).length === this.eventOptions?.length) {
        const selectedIoOptions = this.ioOptions.filter((io) => io.checked);

        if (!isBlank(selectedIoOptions)) {
          const ioOptionsWithGoal = selectedIoOptions.filter((opt) => opt?.goalCategory);
          const goalCategory = !isBlank(ioOptionsWithGoal)
            ? ioOptionsWithGoal[0]?.goalCategory
            : '';
          const selectedEvent = newSelectedOptions.find((ev) => ev.category === goalCategory);

          let selectedEventIndex = -1;
          if (selectedEvent) {
            selectedEventIndex = newSelectedOptions.findIndex((ev) => ev.category === goalCategory);
            selectedEvent.selected = true;
          } else if (newSelectedOptions.length > 0) {
            selectedEventIndex = 0;
            newSelectedOptions[0].selected = true;
          }

          // check all sub cat when main cat is selected
          const mainEvent = newSelectedOptions[selectedEventIndex];
          if (mainEvent && mainEvent.selected) {
            mainEvent.children?.forEach((subCat) => {
              subCat.selected = true;
            });
          }
        }
      }

      if (isCheckForPopup) {
        const prevOptionChecked = prevIoOptions.filter((opt) => opt.checked);
        const currentOptionsChecked = this.filters.ioOptions.filter((fopt) => fopt.checked);
        let restrictPopup = true;
        if (prevOptionChecked.length > 1 && currentOptionsChecked.length === 1) {
          restrictPopup = false;
        }
        if (
          prevOptionChecked.length <= 1 &&
          currentOptionsChecked.length <= 1 &&
          !(prevOptionChecked[0]?.value === currentOptionsChecked[0]?.value)
        ) {
          restrictPopup = false;
        }

        const selectedNewOption = newSelectedOptions.filter((nso) => nso.selected)?.[0] || {};
        const currentSelectedOption = this.eventOptions.filter((ept) => ept.selected)?.[0] || {};

        if (selectedNewOption.event !== currentSelectedOption.event && restrictPopup) {
          this.showEventChangePopup = {
            visible: true,
            description: `Change Events Dropdown selection from ${currentSelectedOption.event} to ${selectedNewOption.event}?`,
            payload: {
              newSelectedOptions,
            },
          };
        }
        this.isEventsLoading = false;
        return;
      }

      const newSelectedEvents = JSON.parse(JSON.stringify(newSelectedOptions));
      newSelectedEvents.forEach((event) => {
        if (event.selected) {
          event.children?.forEach((subCat) => {
            subCat.selected = true;
          });
        }
      });
      this.$store.set('dashboard/filters@eventOptions', newSelectedEvents);
      this.$store.dispatch('common/setAppliedFilterCount', this.appliedFilterCountNew);
      this.isEventsLoading = false;
    },
    updateCreativeOptions(creatives) {
      this.filters.creativeOptions = creatives;
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    animateError(element) {
      // element parameter
      if (element) {
        element.classList.add('error-animation');
        setTimeout(() => {
          element.classList.remove('error-animation');
        }, 500);
      }
    },
    showSuccessMessage(msg) {
      this.message = msg;
      this.messageType = 'success';
      this.displayMessage();
    },
    showErrorMessage(msg) {
      this.message = msg;
      this.messageType = 'error';
      this.displayMessage();
    },
    displayMessage() {
      this.showMessage = true;
      setTimeout(() => {
        this.showMessage = false;
      }, 5000);
    },
    loadEventsAndIosSettings(callback) {
      this.isEventsLoading = true;
      this.$nextTick(() => {
        if (EventBus.eventsLoaded) {
          const cachedEventOptions = getCachedFilters()?.eventOptions;

          if (!cachedEventOptions || cachedEventOptions === null || !cachedEventOptions.length) {
            const selectedIoOptions = this.ioOptions.filter((io) => io.checked);
            if (selectedIoOptions.length === 1) {
              this.isEventLoading = true;
              this.singleCampaignSettings(selectedIoOptions[0].campaignId, true);
              this.changeEventOptions();
              this.$store.dispatch('common/setAppliedFilterCount', this.appliedFilterCountNew);
            } else {
              this.changeEventOptions();
              this.defaultCampaignSettings(true);
              this.selectedCampaignGoal = {};
              this.$store.dispatch('common/setAppliedFilterCount', this.appliedFilterCountNew);
            }
          }

          this.isEventsLoading = false;

          if (callback && typeof callback === 'function') {
            callback();
          }
        } else {
          setTimeout(() => this.loadEventsAndIosSettings(callback), 300);
        }
      });
    },
    getStoredDefaultFilters() {
      return JSON.parse(sessionStorage.getItem('initialReportBuilderFiltersForAP'));
    },
    saveRb2SessionFilters(filters) {
      if (filters) {
        sessionStorage.setItem('rb2_session_filter', JSON.stringify(filters));
      }
    },
    reapplyAdvanceFilterValues(templateFilters) {
      const savedCreatives = (templateFilters.creatives || []).map(String);
      const savedMediaTypes = (templateFilters.mediaTypes || []).map(String);
      const savedAudiences = (templateFilters.audiences || []).map(String);

      const creativeOpts = this.creativeOptions || this.initialFilters?.creativeOptions;
      if (creativeOpts) {
        creativeOpts.forEach((c) => {
          c.checked = savedCreatives.includes(String(c.key));
        });
        this.$store.set('dashboard/filters@creativeOptions', creativeOpts);
      }

      const mediaTypeOpts = this.mediaTypeOptions || this.initialFilters?.mediaTypeOpts;
      if (mediaTypeOpts) {
        mediaTypeOpts.forEach((m) => {
          m.checked = savedMediaTypes.includes(String(m.value));
        });
        this.$store.set('dashboard/filters@mediaTypeOptions', mediaTypeOpts);
      }

      const audienceOpts = this.audienceOptions || this.initialFilters?.audienceOpts;
      if (audienceOpts) {
        audienceOpts.forEach((a) => {
          a.checked = savedAudiences.includes(String(a.value));
        });
        this.$store.set('dashboard/filters@audienceOptions', audienceOpts);
      }
    },
    guardSessionRestoreAgainstLateOverrides(sessionFilters) {
      let triggered = false;
      const onLoadDone = () => {
        if (triggered) return;
        triggered = true;
        EventBus.$off('advance-filter-loading-done', onLoadDone);
        this.reapplyAdvanceFilterValues(sessionFilters);
      };
      EventBus.$on('advance-filter-loading-done', onLoadDone);
    },
    applySessionAttributionOverrides(sessionFilters) {
      if (this.isMniAccount) return;

      this.filters.conversionWindowDisplay = sessionFilters.conversionWindow;
      this.filters.conversionWindowUnit = sessionFilters.conversionWindowUnit;

      if (sessionFilters.methodology) {
        const methodologyOptions = this.$store.copy('dashboard/filters@methodologyOptions');
        const methodologyOption = methodologyOptions.find(
          (mo) => mo.id?.toUpperCase() === sessionFilters.methodology.toUpperCase()
        );
        this.onSelectMethodology(methodologyOption?.id || DEFAULT_METHODOLOGY);
      }

      const totalOrUniqueRadioOptions = this.$store.copy(
        'dashboard/filters@totalOrUniqueRadioOptions'
      );
      if (totalOrUniqueRadioOptions?.length >= 2) {
        totalOrUniqueRadioOptions[0].active = !sessionFilters.showUniques;
        totalOrUniqueRadioOptions[1].active = !!sessionFilters.showUniques;
        this.updateTotalOrUniqueRadioOptions(totalOrUniqueRadioOptions);
      }
    },
    checkIfAllNecessaryDataIsAvailableForNonMni() {
      if (this.startTime === null) {
        this.startTime = Date.now();
      }

      this.$nextTick(async () => {
        if (
          EventBus.iosReady &&
          EventBus.mediaReady &&
          EventBus.creativesReady &&
          EventBus.audienceReady &&
          EventBus.publisherReady
        ) {
          this.isApiCallsLoading = false;

          if (EventBus.fetchSavedReports) {
            this.$emit('update-data-loading', true);
            await this.fetchUserSavedReports(this.advertiser?.id);
            EventBus.fetchSavedReports = false;
          }
          this.filters.template = 'new';
          this.filters = this.genFilters();

          let initFilters = {};

          const initialFilterFromStorage = this.getStoredDefaultFilters();
          if (initialFilterFromStorage !== undefined && initialFilterFromStorage !== null) {
            initFilters = initialFilterFromStorage;
          } else {
            initFilters = JSON.parse(JSON.stringify(this.genFilters()));
            sessionStorage.setItem('initialReportBuilderFiltersForAP', JSON.stringify(initFilters));
          }
          this.resetToDefaultValues(initFilters);
          this.loadEventsAndIosSettings(async () => {
            try {
              const sessionFilters = JSON.parse(sessionStorage.getItem('rb2_session_filter'));
              if (!sessionFilters?.selectedTemplate) {
                this.finalizePageReady();
                return;
              }
              this.isRestoringSession = true;
              await this.onSelectTemplate(sessionFilters.selectedTemplate);
              await this.applyTemplateFilters(sessionFilters);
              this.applySessionAttributionOverrides(sessionFilters);
              this.guardSessionRestoreAgainstLateOverrides(sessionFilters);
              if (isDemoInstance()) {
                await this.setDefaultDates();
              }
              this.isRestoringSession = false;
              this.finalizePageReady();
            } catch (error) {
              this.isRestoringSession = false;
              this.finalizePageReady();
              console.error('Error parsing session filters from session storage:', error);
            }
          });
          this.$nextTick(() => {
            this.$store.dispatch('common/setAppliedFilterCount', this.appliedFilterCountNew);
          });
        } else if (Date.now() - this.startTime < this.retryTimeout) {
          this.isApiCallsLoading = true;
          setTimeout(() => this.checkIfAllNecessaryDataIsAvailableForNonMni(), 300);
        } else {
          this.isApiCallsLoading = false;
          this.finalizePageReady();
          console.error('Retry time exceeded 20 seconds. Some data might still be missing.');
        }
      });
    },
    checkIfAllNecessaryDataIsAvailable() {
      if (this.startTime === null) {
        this.startTime = Date.now();
      }

      this.$nextTick(async () => {
        if (
          EventBus.campaignsReady &&
          EventBus.mediaReady &&
          EventBus.creativesReady &&
          EventBus.audienceReady
        ) {
          this.isApiCallsLoading = false;

          if (EventBus.fetchSavedReports) {
            this.$emit('update-data-loading', true);
            await this.fetchUserSavedReports(this.advertiser?.id);
            EventBus.fetchSavedReports = false;
          }
          this.filters.template = 'new';
          this.filters = this.genFilters();

          const initialFilterFromStorage = localStorage.getItem('initialReportBuilderFilters');
          if (initialFilterFromStorage !== undefined && initialFilterFromStorage !== null) {
            this.initialFilters = JSON.parse(initialFilterFromStorage);
          } else {
            this.initialFilters = JSON.parse(JSON.stringify(this.genFilters()));
            localStorage.setItem(
              'initialReportBuilderFilters',
              JSON.stringify(this.initialFilters)
            );
          }

          this.resetToDefaultValues(this.initialFilters);

          try {
            const sessionFilters = JSON.parse(sessionStorage.getItem('rb2_session_filter'));
            if (sessionFilters?.selectedTemplate) {
              this.isRestoringSession = true;
              await this.onSelectTemplate(sessionFilters.selectedTemplate);
              await this.applyTemplateFilters(sessionFilters);
              this.guardSessionRestoreAgainstLateOverrides(sessionFilters);
              if (isDemoInstance()) {
                await this.setDefaultDates();
              }
              this.isRestoringSession = false;
              this.finalizePageReady();
            } else {
              this.finalizePageReady();
            }
          } catch (error) {
            this.isRestoringSession = false;
            this.finalizePageReady();
            console.error('Error parsing session filters from session storage:', error);
          }

          this.$nextTick(() => {
            this.$store.dispatch('common/setAppliedFilterCount', this.appliedFilterCountNew);
          });
        } else if (Date.now() - this.startTime < this.retryTimeout) {
          this.isApiCallsLoading = true;
          setTimeout(() => this.checkIfAllNecessaryDataIsAvailable(), 300);
        } else {
          this.isApiCallsLoading = false;
          this.finalizePageReady();
          console.error('Retry time exceeded 20 seconds. Some data might still be missing.');
        }
      });
    },
    genFilters() {
      const ioOptions = this.$store.copy('dashboard/filters@ioOptions');
      const totalOrUniqueRadioOptions = this.$store.copy(
        'dashboard/filters@totalOrUniqueRadioOptions'
      );
      const showUniques = this.$store.copy('dashboard/filters@showUniques');
      if (showUniques) {
        totalOrUniqueRadioOptions[1].active = true;
        totalOrUniqueRadioOptions[0].active = false;
      }
      const intervalOptions = this.$store.copy('dashboard/filters@intervalOptions');
      const interval = this.$store.copy('dashboard/filters@interval');
      intervalOptions.forEach((a) => {
        a.active = interval === a.id;
      });

      const methodologyOptions = this.$store.copy('dashboard/filters@methodologyOptions');
      const methodology = this.$store.copy('dashboard/filters@methodology');
      methodologyOptions.forEach((mo) => {
        mo.active = methodology === mo.id;
      });
      const campaignOptions = this.$store.copy('dashboard/filters@campaignOptions');
      const audienceOpts = this.$store.copy('dashboard/filters@audienceOptions');
      const mediaTypeOptions = this.$store.copy('dashboard/filters@mediaTypeOptions');
      return {
        template: this.$store.copy('dashboard/filters@template'),
        intervalOptions,
        interval,
        showUniques,
        methodology,
        ioOptions,
        campaignOptions,
        methodologyOptions,
        conversionWindowUnit: this.$store.copy('dashboard/filters@conversionWindowUnit'),
        conversionWindow: this.$store.copy('dashboard/filters@conversionWindow'),
        conversionWindowDisplay: this.$store.copy('dashboard/filters@conversionWindowDisplay'),
        mediaTypeOptions,
        audienceOpts,
        creativeOptions: this.$store.copy('dashboard/filters@creativeOptions'),
        publisherOptions: this.$store.copy('dashboard/filters@publisherOptions'),
        eventOptions: this.$store.copy('dashboard/filters@eventOptions'),
        attributionOptions: 'full',
        totalOrUniqueRadioOptions,
        adGroupOptions: this.$store.copy('dashboard/filters@adGroupOptions'),
      };
    },
    onAdvanceFilterApply(applyFilters) {
      this.triggerFilterChangeOnCampaign = false;
      this.handleFilterChangeOnExistingTemplate();
      if (this.isMniAccount) {
        this.filters.campaignOptions = applyFilters.campaignOptions;

        this.$store.set('dashboard/filters@campaignOptions', applyFilters.campaignOptions);

        this.selectedFilters.campaignOptions = applyFilters.campaignOptions.filter(
          (item) => item.checked
        );
      } else {
        this.filters.ioOptions = applyFilters.ioOptions;
        this.$store.set(
          'dashboard/filters@ioOptions',
          this.filters.ioOptions.map((option) => ({ ...option, disabled: false }))
        );
        this.selectedFilters.ioOptions = applyFilters.ioOptions.filter((item) => item.checked);
      }
      this.filters.adGroupOptions = applyFilters.adGroupOptions;
      this.filters.mediaTypeOptions = applyFilters.mediaTypeOptions;
      this.filters.creativeOptions = applyFilters.creativeOptions;
      this.filters.audienceOptions = applyFilters.audienceOptions;
      this.filters.publisherOptions = applyFilters.publisherOptions;

      if (!this.isMniAccount) {
        // Fix: top-filter-section skips the store update for adGroupOptions in the isNewReportBuilder path,
        // so the main screen dropdown's :value stayed out of sync with what advanced filter applied.
        this.$store.set('dashboard/filters@adGroupOptions', applyFilters.adGroupOptions);
        this.$store.set('dashboard/filters@pixelOptions', applyFilters.pixelOptions);
        this.$store.set('dashboard/filters@audienceOptions', applyFilters.audienceOptions);
        this.$store.set('dashboard/filters@creativeOptions', applyFilters.creativeOptions);
        this.$store.set('dashboard/filters@keywordOptions', applyFilters.keywordOptions);
        this.$store.set('dashboard/filters@publisherOptions', applyFilters.publisherOptions);
        this.$store.set('dashboard/filters@mediaTypeOptions', applyFilters.mediaTypeOptions);
      }

      this.$store.dispatch('common/setAppliedFilterCount', this.appliedFilterCountNew);
    },
    async setDefaultDates() {
      let computedDate = await get('dashboard/dates');
      const defaults = generateFlipDefaultDates();
      let { startDate, endDate, dateRangeOption } = defaults;
      const { compareWith, defaultDateRangeOption } = defaults;

      if (isDemoInstance()) {
        const advertiserKeys = Object.keys(this.demoAdvertisers).map((k) => parseInt(k, 10));
        const demoAdvertiserId = parseInt(localStorage.getItem(KEY_DEMO_ADVERTISER_ID), 10);
        const demoAdvertiserMappingArr = advertiserKeys.includes(demoAdvertiserId)
          ? this.demoAdvertisers[demoAdvertiserId]
          : null;
        let demoAdvertiserMapping;
        if (Array.isArray(demoAdvertiserMappingArr) && demoAdvertiserMappingArr.length > 0) {
          demoAdvertiserMapping = demoAdvertiserMappingArr.find((m) => m.solutionId === 1);
          if (!demoAdvertiserMapping) {
            [demoAdvertiserMapping] = demoAdvertiserMappingArr;
          }
        } else {
          demoAdvertiserMapping = null;
        }

        const isTouchTemplate = [ALL_TOUCH_REPORT, LAST_TOUCH_REPORT].includes(
          this.selectedTemplateOption
        );
        if (
          isTouchTemplate &&
          demoAdvertiserMapping?.touchReportStartDate &&
          demoAdvertiserMapping?.touchReportEndDate
        ) {
          startDate = moment(demoAdvertiserMapping.touchReportStartDate);
          endDate = moment(demoAdvertiserMapping.touchReportEndDate);
          dateRangeOption = RANGE_CUSTOM;
        } else {
          startDate =
            demoAdvertiserMapping?.startDate && demoAdvertiserMapping?.endDate
              ? moment(demoAdvertiserMapping.startDate)
              : startDate;
          endDate =
            demoAdvertiserMapping?.startDate && demoAdvertiserMapping?.endDate
              ? moment(demoAdvertiserMapping.endDate)
              : endDate;
          dateRangeOption =
            demoAdvertiserMapping?.startDate && demoAdvertiserMapping?.endDate
              ? RANGE_CUSTOM
              : RANGE_LAST_30_DAYS;
        }
      }
      computedDate = { ...this.dates, startDate, endDate, dateRangeOption, compareWith };
      this.$store.set('dashboard/dates', {
        ...this.dates,
        ...computedDate,
      });
      this.dateKey += 1;

      this.checkDateForScheduler(defaultDateRangeOption);
    },
    checkDateForScheduler(dateRangeOption) {
      if (Number(dateRangeOption) === 0) {
        // this is custom date, we disable schedule report
        // exception: demo touch reports use forced custom dates but should still allow scheduling
        if (!this.isDemoTouchReport) {
          this.resetScheduleFilter();
          this.disableScheduleReport = true;
        } else {
          this.disableScheduleReport = false;
        }
        this.disableSaveReportTemplate = false;
        this.disableSaveReportBasedOnSchedReport = false;
      } else {
        this.disableSaveReportTemplate = false;
        this.disableScheduleReport = false;
        this.disableSaveReportBasedOnSchedReport = false;
      }
    },
    resetToDefaultValues(initialFilters) {
      if (!initialFilters || initialFilters === null) return;

      this.setDefaultDates();
      this.filters.conversionWindowUnit = initialFilters?.conversionWindowUnit;
      this.filters.conversionWindowDisplay = initialFilters?.conversionWindowDisplay;
      this.filters.interval = initialFilters?.interval;

      // For MNI, initialFilters might have empty arrays if it was cached before the EventBus populated them.
      // Fallback to the current Vuex/local options to retain the actual items, just resetting their checked states.
      const mediaOptsSource =
        (initialFilters?.mediaTypeOptions?.length > 0 && initialFilters.mediaTypeOptions) ||
        this.mediaTypeOptions ||
        [];
      const creativeOptsSource =
        (initialFilters?.creativeOptions?.length > 0 && initialFilters.creativeOptions) ||
        this.creativeOptions ||
        [];
      const audienceOptsSource =
        (initialFilters?.audienceOpts?.length > 0 && initialFilters.audienceOpts) ||
        (initialFilters?.audienceOptions?.length > 0 && initialFilters.audienceOptions) ||
        this.audienceOptions ||
        [];
      const publisherOptsSource =
        (initialFilters?.publisherOptions?.length > 0 && initialFilters.publisherOptions) ||
        this.publisherOptions ||
        [];

      const resetMedia = mediaOptsSource.map((opt) => ({ ...opt, checked: true }));
      const resetCreative = creativeOptsSource.map((opt) => ({ ...opt, checked: false }));
      const resetAudience = audienceOptsSource.map((opt) => ({ ...opt, checked: false }));
      const resetPublisher = publisherOptsSource.map((opt) => ({ ...opt, checked: false }));

      if (!this.isMniAccount) {
        // reset total uniq
        const totalOrUniqueRadioOptions = this.$store.copy(
          'dashboard/filters@totalOrUniqueRadioOptions'
        );
        if (initialFilters.showUniques) {
          totalOrUniqueRadioOptions[0].active = false;
          totalOrUniqueRadioOptions[1].active = true;
        } else {
          totalOrUniqueRadioOptions[0].active = true;
          totalOrUniqueRadioOptions[1].active = false;
        }
        this.updateTotalOrUniqueRadioOptions(totalOrUniqueRadioOptions);

        // reset methodology
        const methodologyOptions = this.$store.copy('dashboard/filters@methodologyOptions');
        let methodologyOption = {};

        methodologyOption = methodologyOptions.find((mo) => {
          return mo.id.toUpperCase() === initialFilters.methodology?.toUpperCase();
        });
        const methodologyOptionId = methodologyOption?.id;
        this.onSelectMethodology(methodologyOptionId || DEFAULT_METHODOLOGY);
      }

      if (this.isMniAccount) {
        this.$store.set('dashboard/filters@campaignOptions', initialFilters?.campaignOptions);
      } else {
        this.$store.set('dashboard/filters@ioOptions', initialFilters?.ioOptions);
        if (
          Array.isArray(initialFilters?.adGroupOptions) &&
          initialFilters?.adGroupOptions.length > 0
        ) {
          this.$store.set('dashboard/filters@adGroupOptions', initialFilters?.adGroupOptions);
        }
      }

      // Always restore advanced filters to their intended default states for BOTH MNI and non-MNI
      this.filters.mediaTypeOptions = resetMedia;
      this.filters.creativeOptions = resetCreative;
      this.filters.audienceOptions = resetAudience;
      this.filters.publisherOptions = resetPublisher;

      this.$store.set('dashboard/filters@mediaTypeOptions', resetMedia);
      this.$store.set('dashboard/filters@creativeOptions', resetCreative);
      this.$store.set('dashboard/filters@audienceOptions', resetAudience);
      this.$store.set('dashboard/filters@publisherOptions', resetPublisher);

      this.$store.dispatch('dashboard/resetToDefaultAPFilters');
      this.resetEventSelection();
      this.resetCampaignSelection(initialFilters);
    },

    resetAdvanceFilterOptions(initialFilters) {
      this.$nextTick(() => {
        if (this.isMniAccount) {
          this.filters.campaignOptions = initialFilters.campaignOptions;
          this.$store.set('dashboard/filters@campaignOptions', initialFilters?.campaignOptions);
          this.selectedFilters.campaignOptions = (initialFilters?.campaignOptions || []).filter(
            (c) => c.checked
          );
        } else {
          this.filters.ioOptions = initialFilters.ioOptions;
          this.$store.set('dashboard/filters@ioOptions', initialFilters?.ioOptions);
          this.selectedFilters.ioOptions = (initialFilters?.ioOptions || []).filter(
            (io) => io.checked
          );
          this.filters.adGroupOptions = initialFilters.adGroupOptions;
          if (
            Array.isArray(initialFilters?.adGroupOptions) &&
            initialFilters?.adGroupOptions.length > 0
          ) {
            this.$store.set('dashboard/filters@adGroupOptions', initialFilters?.adGroupOptions);
          }
        }

        // Fallback to current options if initialFilters are empty (especially for MNI)
        const mediaOptsSource =
          (initialFilters?.mediaTypeOptions?.length > 0 && initialFilters.mediaTypeOptions) ||
          this.mediaTypeOptions ||
          [];
        const creativeOptsSource =
          (initialFilters?.creativeOptions?.length > 0 && initialFilters.creativeOptions) ||
          this.creativeOptions ||
          [];
        const audienceOptsSource =
          (initialFilters?.audienceOpts?.length > 0 && initialFilters.audienceOpts) ||
          (initialFilters?.audienceOptions?.length > 0 && initialFilters.audienceOptions) ||
          this.audienceOptions ||
          [];
        const publisherOptsSource =
          (initialFilters?.publisherOptions?.length > 0 && initialFilters.publisherOptions) ||
          this.publisherOptions ||
          [];

        // Apply proper checked states
        const resetMedia = mediaOptsSource.map((opt) => ({ ...opt, checked: true }));
        const resetCreative = creativeOptsSource.map((opt) => ({ ...opt, checked: false }));
        const resetAudience = audienceOptsSource.map((opt) => ({ ...opt, checked: false }));
        const resetPublisher = publisherOptsSource.map((opt) => ({ ...opt, checked: false }));

        this.filters.mediaTypeOptions = resetMedia;
        this.$store.set('dashboard/filters@mediaTypeOptions', resetMedia);

        this.filters.creativeOptions = resetCreative;
        this.$store.set('dashboard/filters@creativeOptions', resetCreative);

        this.filters.audienceOptions = resetAudience;
        this.$store.set('dashboard/filters@audienceOptions', resetAudience);

        this.filters.publisherOptions = resetPublisher;
        this.$store.set('dashboard/filters@publisherOptions', resetPublisher);

        this.$store.dispatch('common/setAppliedFilterCount', this.appliedFilterCountNew);
      });
    },
    resetCampaignSelection(initialFilters) {
      this.$nextTick(() => {
        if (this.isMniAccount) {
          this.filters.campaignOptions = initialFilters.campaignOptions;
          this.$store.set('dashboard/filters@campaignOptions', initialFilters?.campaignOptions);
          this.selectedFilters.campaignOptions = (initialFilters?.campaignOptions || []).filter(
            (c) => c.checked
          );
        } else {
          this.filters.ioOptions = initialFilters.ioOptions;
          this.$store.set('dashboard/filters@ioOptions', initialFilters?.ioOptions);
          this.selectedFilters.ioOptions = (initialFilters?.ioOptions || []).filter(
            (io) => io.checked
          );
        }
      });
    },
    resetEventSelection() {
      const { event: defaultEvent, category: defaultCategory } = this.defaultEventSelected;
      this.eventOptions.forEach((event) => {
        const isSelected = event.event === defaultEvent && event.category === defaultCategory;
        event.selected = isSelected;
        if (event.children && event.children.length) {
          event.children.forEach((subCategory) => {
            subCategory.selected = isSelected;
          });
        }
      });
    },

    async onSelectTemplate(template) {
      this.selectedTemplateOption = template;
      this.disableInterval = false;
      this.disabledAllEvents = false;
      this.selectedTemplateType = '';
      this.disableLookBackWindow = false;
      this.disabledAdvanceFilter = false;
      this.singleSelectModeDimension = false;
      switch (true) {
        case PRE_DEFINED_TEMPLATES.includes(template):
          await this.setupPredefinedTemplate(template);
          if (!this.isMniAccount) {
            this.changeEventOptions();
          }
          break;
        case template === NEW_REPORT:
          await this.setupNewTemplate();
          if (!this.isMniAccount) {
            this.changeEventOptions();
          }
          break;
        default:
          await this.loadExistingTemplate(template);
      }
      await this.$nextTick();
      this.filters.template = template;
      this.$store.set('dashboard/filters@template', template);
      this.$store.dispatch('common/setAppliedFilterCount', this.appliedFilterCountNew);
    },
    async setupPredefinedTemplate(template) {
      this.reportName = '';
      this.selectedTemplateId = '';

      // disable dimensions
      if (template !== CAT_SUBCAT_REPORT) {
        this.selectedDimensionsOptions = [];
        this.disableAllDimensions = true;
        this.singleSelectMode = true;
        this.disableInterval = true;
      } else {
        this.disableAllDimensions = false;
        this.selectedDimensionsOptions = ['creative'];
        this.singleSelectMode = false;
        this.disableInterval = false;
      }

      // disable metrics
      this.selectedMetricsOptions = [];
      this.selectAllMetrics = false;
      this.disabledAllMetrics = true;
      this.disabledAllEvents = false;

      // reset to default values
      if (this.isMniAccount) {
        this.resetToDefaultValues(this.initialFilters);
      } else {
        this.resetToDefaultValues(this.getStoredDefaultFilters());
      }
      // disable save template checkbox
      this.disableSaveReportTemplate = false;
      this.resetScheduleFilter();
      this.exportNSendReport = false;

      if (
        template === FOOT_TRAFFIC_ATTRIBUTED_VISITS_REPORT ||
        template === FOOT_TRAFFIC_SUMMARY_REPORT
      ) {
        this.eventOptions.forEach((event) => {
          if (event.event === LOC_VISIT_EVENT && event.category === LOC_VISIT_EVENT) {
            event.selected = true;
            if (event.children && event.children.length > 0) {
              event.children.forEach((subCategory) => {
                subCategory.selected = true;
              });
            }
          } else {
            event.selected = false;
            if (event.children && event.children.length > 0) {
              event.children.forEach((subCategory) => {
                subCategory.selected = false;
              });
            }
          }
        });
      }

      if (PRE_DEFINED_TEMPLATES_AUDIENCE_INSIGHTS.includes(template)) {
        if (![CAT_SUBCAT_REPORT, FOOT_TRAFFIC_SUMMARY_REPORT].includes(template)) {
          this.disabledAllEvents = true;
          if (AUDIENCE_INSIGHTS_CAMPAIGNS_REPORT.includes(template)) {
            this.disableLookBackWindow = true;
            this.filters.conversionWindowDisplay = null;
            this.filters.conversionWindow = null;
            this.filters.conversionWindowUnit = null;
          }
          if (AUDIENCE_INSIGHTS_CONVERSION_REPORT.includes(template)) {
            this.disabledAdvanceFilter = true;
          }

          this.eventOptions.forEach((event) => {
            event.selected = false;
            if (event.children && event.children.length > 0) {
              event.children.forEach((subCategory) => {
                subCategory.selected = false;
              });
            }
          });
        }
      }

      if (template === CAT_SUBCAT_REPORT) {
        this.eventOptions.forEach((event) => {
          if (event.event === 'Checkouts') {
            event.selected = true;
            if (event.children && event.children.length > 0) {
              event.children.forEach((subCategory) => {
                subCategory.selected = true;
              });
            }
          }
        });
      }

      await this.$nextTick();
      if (!this.isMniAccount) {
        await this.fetchAdOptionsFromCampaigns(null);
      }
      if (this.isDemoTouchReport) {
        this.runNow = true;
      } else {
        this.runNow = false;
      }
      this.saveTemplate = false;
      this.disableSaveReportBasedOnSchedReport = false;
    },
    async setupNewTemplate() {
      this.isInitialCampaignChange = true;
      this.isInitialAdGChange = true;
      this.isInitialFilterChange = true;
      this.isInitialConversionWindowChange = true;
      this.isInitialConversionUnitChange = true;

      this.reportName = '';
      this.selectedTemplateId = '';
      this.disableAllDimensions = false;
      this.selectedDimensionsOptions = ['creative'];
      this.selectedReportKind = 'creative';
      this.setMetricOpts();
      this.exportNSendReport = false;
      this.disabledAllMetrics = false;
      this.disabledAllEvents = false;
      this.selectAllMetrics = true;
      this.singleSelectMode = false;
      this.disableSaveReportTemplate = false;
      if (this.isMniAccount) {
        this.resetToDefaultValues(this.initialFilters);
      } else {
        this.resetToDefaultValues(this.getStoredDefaultFilters());
      }
      this.setMetricOptsForEvents(this.reportMetricsOptions);
      this.toggleSelectAllMetrics();
      this.resetScheduleFilter();

      await this.$nextTick();
      if (!this.isMniAccount) {
        await this.fetchAdOptionsFromCampaigns(null);
      }
      this.runNow = true;
      this.saveTemplate = false;
      this.disableSaveReportBasedOnSchedReport = false;
    },
    async loadExistingTemplate(templateId) {
      this.initialFilterChange = true;
      if (!this.isMniAccount) {
        this.isAdvanceFilterLoading = true;
        this.$emit('update-data-loading', true);
      }
      this.singleSelectModeDimension = false;
      this.reportDimensionsOptions = this.reportDimensionsOptions.filter(
        (dimension) => !['day', 'daypart'].includes(dimension.id)
      );
      this.selectedDimensionsOptions = this.selectedDimensionsOptions.filter(
        (dimension) => !['day', 'daypart'].includes(dimension)
      );
      this.selectedTemplateType = '';
      this.selectAllMetrics = false;
      this.disableAllDimensions = false;
      this.disabledAllMetrics = false;
      this.disabledAllEvents = false;
      this.disableSaveReportTemplate = false;
      this.singleSelectMode = false;
      this.exportNSendReport = false;

      this.selectedTemplateId = templateId;

      const selectedOption = this.templateOptions.find((option) => option.id === templateId);

      if (selectedOption) {
        // Simplified conditional check.
        if (selectedOption.schedule !== null) {
          this.applyScheduleFilters(selectedOption);
        } else {
          this.resetScheduleFilter();
        }
        this.adGroupsLoaded = false;
        await this.applyTemplateFilters(selectedOption.filters);
        await this.$nextTick();
        if (PRE_DEFINED_TEMPLATES.includes(this.selectedTemplateType)) {
          this.runNow = false;
        } else {
          this.runNow = true;
        }
        this.reportName = selectedOption.name;
        this.saveTemplate = false;
        this.disableSaveReportBasedOnSchedReport = false;
        this.initialFilterChange = false;
      }
    },
    isEventDisabled(event) {
      if (this.disabledAllEvents) {
        return true;
      }

      const isDisablingEvent = ['Clicks', 'Completions', 'Video Completions'].includes(event.event);
      const isDisablingTemplate = [ALL_TOUCH_REPORT, LAST_TOUCH_REPORT, CAT_SUBCAT_REPORT].includes(
        this.filters.template
      );
      const hasSelectedType = this.selectedTemplateType.length > 0;

      if (isDisablingEvent && (isDisablingTemplate || hasSelectedType)) {
        return true;
      }
      const isFootTrafficTemplate =
        this.filters.template === FOOT_TRAFFIC_ATTRIBUTED_VISITS_REPORT ||
        this.filters.template === FOOT_TRAFFIC_SUMMARY_REPORT;
      const isTheAllowedFootTrafficEvent = event.event === 'Location Visits';

      if (
        (isFootTrafficTemplate && !isTheAllowedFootTrafficEvent) ||
        ((this.selectedTemplateType === FOOT_TRAFFIC_ATTRIBUTED_VISITS_REPORT ||
          this.selectedTemplateType === FOOT_TRAFFIC_SUMMARY_REPORT) &&
          !isTheAllowedFootTrafficEvent)
      ) {
        return true;
      }

      return false;
    },
    applyScheduleFilters(selectedOption) {
      this.scheduleReport = true;
      this.selectedFrequency = selectedOption.schedule;
      this.scheduleStartDate = selectedOption.scheduleStartDate;
      this.scheduleEndDate = selectedOption.scheduleEndDate;
      this.emailsToSched = selectedOption.emailTo.split(',').map((email) => email?.trim());
    },
    resetScheduleFilter() {
      this.scheduleReport = false;
      this.selectedFrequency = 'select';
      this.scheduleStartDate = '';
      this.scheduleEndDate = '';
      this.emailsToSched = [];
    },
    onSelectInterval(interval) {
      this.handleFilterChangeOnExistingTemplate();
      this.filters.interval = interval;
    },
    toggleAdvanceFilterModal() {
      this.filters.isNewReportBuilder = true;
      this.filters.filtersToHide = [MEDIATYPE_FILTER, AUDIENCE_FILTER];
      if (AUDIENCE_INSIGHTS_CAMPAIGNS_REPORT.includes(this.selectedTemplateType)) {
        this.filters.selectedTemplateType = this.selectedTemplateType;
      } else {
        this.filters.selectedTemplateType = '';
      }
      EventBus.$emit('show-advance-filter', this.filters);
    },
    onClearAdvanceFilters() {
      this.handleFilterChangeOnExistingTemplate();
      if (this.isMniAccount) {
        this.resetAdvanceFilterOptions(this.initialFilters);
      } else {
        this.resetAdvanceFilterOptions(this.getStoredDefaultFilters());
        this.$nextTick(async () => {
          this.getAdvanceFilterDataForNewReportBuilder(this.filters);
          if (!this.hasPreviousFilters()) {
            await this.fetchAdOptionsFromCampaigns();
          } else {
            await this.fetchAdOptionsFromCampaigns(null);
          }
        });
      }
    },
    hasPreviousFilters() {
      try {
        const previousFilters = JSON.parse(sessionStorage.getItem('rb2_session_filter'));
        return !!previousFilters;
      } catch (e) {
        return false;
      }
    },
    onResetToDefault() {
      sessionStorage.removeItem('rb2_session_filter');
      // Re-run the template-selection flow so filters return to the current
      // template's defaults.
      const currentTemplate = this.filters.template;
      if (!currentTemplate) return;
      this.onSelectTemplate(currentTemplate);
    },
    getReportDimensionsOptions() {
      let dimensions = [...NEW_REPORT_OPTIONS];
      if (!ACCOUNTS_TO_SHOW.includes(this.account?.id)) {
        dimensions = NEW_REPORT_OPTIONS.filter((item) => item.id !== 'channel');
      }
      if (this.isMniAccount) {
        dimensions = NEW_REPORT_OPTIONS.filter((item) => item.id !== 'audience');
        dimensions.splice(5, 0, { id: 'audience', name: 'Placement', type: 'performance' });
      }
      return dimensions;
    },
    setMetricOptsForEvents(reportMetricsOptions) {
      let opts = [...reportMetricsOptions]; // Create a shallow copy of the array to avoid direct mutation

      const hasVCR = this.eventOptions.some((event) => event.category === 'vcr');
      const hasCTR = this.eventOptions.some((event) => event.category === 'ctr');

      opts = opts.filter((metric) => {
        if ((hasVCR && metric.key === 'completions') || (hasCTR && metric.key === 'clicks')) {
          return false; // Exclude this item
        }
        return true; // Keep other items
      });

      let resetMetrics = true;
      this.selectedEvent.forEach((event) => {
        if (
          event.eventLabel === 'Leads' ||
          event.eventLabel === 'Checkouts' ||
          event.eventLabel === 'Page Visits'
        ) {
          resetMetrics = false;
        }
      });

      if (resetMetrics) {
        // Uncomment if needed
      }

      if (this.selectedEvent.length === 0) {
        opts = opts.filter(
          (item) =>
            ![
              'leadTimeToConvert',
              'leadTotalExposures',
              // 'lead',
              'leadConversionRate',
              'leadCpa',
              'leadPm',
              'checkoutTimeToConvert',
              'checkoutTotalExposures',
              // 'checkout',
              'checkoutConversionRate',
              'checkoutCpa',
              'checkoutPm',
              'pageVisitTimeToConvert',
              'pageVisitTotalExposures',
              // 'pageVisit',
              'pageVisitConversionRate',
              'pageVisitCpa',
              'pageVisitPm',
              // location visit
              'locationVisitTimeToConvert',
              'locationVisitTotalExposures',
              'locationVisitConversionRate',
              'locationVisitCpa',
              'locationVisitPm',
              // Activity
              'activityTimeToConvert',
              'activityTotalExposures',
              'activityConversionRate',
              'activityCpa',
              'activityPm',
            ].includes(item.key)
        );
      } else if (!resetMetrics) {
        opts = opts.filter((item) => !['conversionRate', 'cpa', 'pm'].includes(item.key));
      }

      // Loop through selectedEvent and add metrics if necessary
      this.selectedEvent.forEach((event) => {
        if (event.eventLabel === 'Leads') {
          // Add metrics related to time to convert and total exposures when overall is selected
          if (
            !opts.some((item) => item.key === 'leadTimeToConvert') &&
            this.selectedReportKind === 'overall'
          ) {
            opts.push({
              key: 'leadTimeToConvert',
              name: 'Leads Time to Convert',
              value: 'Leads Time to Convert',
              formatter: { type: 'number', format: '0,0.[00]' },
              checked: true,
            });
          }

          if (
            !opts.some((item) => item.key === 'leadTotalExposures') &&
            this.selectedReportKind === 'overall'
          ) {
            opts.push({
              key: 'leadTotalExposures',
              name: 'Leads Total Exposures',
              value: 'Leads Total Exposures',
              formatter: { type: 'number', format: '0,0.[00]' },
              checked: true,
            });
          }

          if (!opts.some((item) => item.key === 'leadConversionRate')) {
            opts.push({
              key: 'leadConversionRate',
              name: '(%)Lead Conversion Rate',
              value: 'Lead Conversion Rate',
              formatter: { type: 'number', format: '0.00' },
              checked: true,
            });
          }

          if (!opts.some((item) => item.key === 'leadCpa')) {
            opts.push({
              key: 'leadCpa',
              name: 'Lead CPA',
              value: 'Lead CPA',
              formatter: { type: 'number', format: '0.00' },
              checked: true,
            });
          }

          if (!opts.some((item) => item.key === 'leadPm')) {
            opts.push({
              key: 'leadPm',
              name: 'Lead Performance Multiplier',
              value: 'Lead Performance Multiplier',
              formatter: { type: 'number', format: '0,0.[00]' },
              checked: true,
            });
          }
        } else if (event.eventLabel === 'Checkouts') {
          // Add metrics related to time to convert and total exposures when overall is selected
          if (
            !opts.some((item) => item.key === 'checkoutTimeToConvert') &&
            this.selectedReportKind === 'overall'
          ) {
            opts.push({
              key: 'checkoutTimeToConvert',
              name: 'Checkouts Time to Convert',
              value: 'Checkouts Time to Convert',
              formatter: { type: 'number', format: '0,0.[00]' },
              checked: true,
            });
          }

          if (
            !opts.some((item) => item.key === 'checkoutTotalExposures') &&
            this.selectedReportKind === 'overall'
          ) {
            opts.push({
              key: 'checkoutTotalExposures',
              name: 'Checkouts Total Exposures',
              value: 'Checkouts Total Exposures',
              formatter: { type: 'number', format: '0,0.[00]' },
              checked: true,
            });
          }

          if (!opts.some((item) => item.key === 'checkoutConversionRate')) {
            opts.push({
              key: 'checkoutConversionRate',
              name: '(%)Checkout Conversion Rate',
              value: 'Checkout Conversion Rate',
              formatter: { type: 'number', format: '0.00' },
              checked: true,
            });
          }

          if (!opts.some((item) => item.key === 'checkoutCpa')) {
            opts.push({
              key: 'checkoutCpa',
              name: 'Checkout CPA',
              value: 'Checkout CPA',
              formatter: { type: 'number', format: '0.00' },
              checked: true,
            });
          }

          if (!opts.some((item) => item.key === 'checkoutPm')) {
            opts.push({
              key: 'checkoutPm',
              name: 'Checkout Performance Multiplier',
              value: 'Checkout Performance Multiplier',
              formatter: { type: 'number', format: '0,0.[00]' },
              checked: true,
            });
          }
        } else if (event.eventLabel === 'Page Visits') {
          // Add metrics related to time to convert and total exposures when overall is selected
          if (
            !opts.some((item) => item.key === 'pageVisitTimeToConvert') &&
            this.selectedReportKind === 'overall'
          ) {
            opts.push({
              key: 'pageVisitTimeToConvert',
              name: 'Page Visits Time to Convert',
              value: 'Page Visits Time to Convert',
              formatter: { type: 'number', format: '0,0.[00]' },
              checked: true,
            });
          }

          if (
            !opts.some((item) => item.key === 'pageVisitTotalExposures') &&
            this.selectedReportKind === 'overall'
          ) {
            opts.push({
              key: 'pageVisitTotalExposures',
              name: 'Page Visits Total Exposures',
              value: 'Page Visits Total Exposures',
              formatter: { type: 'number', format: '0,0.[00]' },
              checked: true,
            });
          }

          if (!opts.some((item) => item.key === 'pageVisitConversionRate')) {
            opts.push({
              key: 'pageVisitConversionRate',
              name: '(%)Page Visit Conversion Rate',
              value: 'Page Visit Conversion Rate',
              formatter: { type: 'number', format: '0.00' },
              checked: true,
            });
          }

          if (!opts.some((item) => item.key === 'pageVisitCpa')) {
            opts.push({
              key: 'pageVisitCpa',
              name: 'Page Visit CPA',
              value: 'Page Visit CPA',
              formatter: { type: 'number', format: '0.00' },
              checked: true,
            });
          }

          if (!opts.some((item) => item.key === 'pageVisitPm')) {
            opts.push({
              key: 'pageVisitPm',
              name: 'Page Visit Performance Multiplier',
              value: 'Page Visit Performance Multiplier',
              formatter: { type: 'number', format: '0,0.[00]' },
              checked: true,
            });
          }
        } else if (event.eventLabel === 'Revenue') {
          if (!opts.some((item) => item.key === 'roas')) {
            opts.push({
              key: 'roas',
              name: '(%)ROAS',
              value: 'ROAS',
              formatter: { type: 'number', format: '0' },
              checked: true,
            });
          }
        } else if (event.eventLabel === 'Location Visits') {
          // Add metrics related to time to convert and total exposures when overall is selected
          if (
            !opts.some((item) => item.key === 'locationVisitTimeToConvert') &&
            this.selectedReportKind === 'overall'
          ) {
            opts.push({
              key: 'locationVisitTimeToConvert',
              name: 'Location Visits Time to Convert',
              value: 'Location Visits Time to Convert',
              formatter: { type: 'number', format: '0,0.[00]' },
              checked: true,
            });
          }

          if (
            !opts.some((item) => item.key === 'locationVisitTotalExposures') &&
            this.selectedReportKind === 'overall'
          ) {
            opts.push({
              key: 'locationVisitTotalExposures',
              name: 'Location Visits Total Exposures',
              value: 'Location Visits Total Exposures',
              formatter: { type: 'number', format: '0,0.[00]' },
              checked: true,
            });
          }

          if (!opts.some((item) => item.key === 'locationVisitConversionRate')) {
            opts.push({
              key: 'locationVisitConversionRate',
              name: '(%)Location Visits Conversion Rate',
              value: 'Location Visits Conversion Rate',
              formatter: { type: 'number', format: '0.00' },
              checked: true,
            });
          }

          if (!opts.some((item) => item.key === 'locationVisitCpa')) {
            opts.push({
              key: 'locationVisitCpa',
              name: 'Location Visits CPA',
              value: 'Location Visits CPA',
              formatter: { type: 'number', format: '0.00' },
              checked: true,
            });
          }

          if (!opts.some((item) => item.key === 'locationVisitPm')) {
            opts.push({
              key: 'locationVisitPm',
              name: 'Location Visits Performance Multiplier',
              value: 'Location Visits Performance Multiplier',
              formatter: { type: 'number', format: '0,0.[00]' },
              checked: true,
            });
          }
        } else if (event.eventLabel === 'Activities') {
          // Add metrics related to time to convert and total exposures when overall is selected
          if (
            !opts.some((item) => item.key === 'activityTimeToConvert') &&
            this.selectedReportKind === 'overall'
          ) {
            opts.push({
              key: 'activityTimeToConvert',
              name: 'Activities Time to Convert',
              value: 'Activities Time to Convert',
              formatter: { type: 'number', format: '0,0.[00]' },
              checked: true,
            });
          }

          if (
            !opts.some((item) => item.key === 'activityTotalExposures') &&
            this.selectedReportKind === 'overall'
          ) {
            opts.push({
              key: 'activityTotalExposures',
              name: 'Activities Total Exposures',
              value: 'Activities Total Exposures',
              formatter: { type: 'number', format: '0,0.[00]' },
              checked: true,
            });
          }

          if (!opts.some((item) => item.key === 'activityConversionRate')) {
            opts.push({
              key: 'activityConversionRate',
              name: '(%)Activities Conversion Rate',
              value: 'Activities Conversion Rate',
              formatter: { type: 'number', format: '0.00' },
              checked: true,
            });
          }

          if (!opts.some((item) => item.key === 'activityCpa')) {
            opts.push({
              key: 'activityCpa',
              name: 'Activities CPA',
              value: 'Activities CPA',
              formatter: { type: 'number', format: '0.00' },
              checked: true,
            });
          }

          if (!opts.some((item) => item.key === 'activityPm')) {
            opts.push({
              key: 'activityPm',
              name: 'Activities Performance Multiplier',
              value: 'Activities Performance Multiplier',
              formatter: { type: 'number', format: '0,0.[00]' },
              checked: true,
            });
          }
        }
      });

      // Now check for any events that were removed and remove their metrics
      if (!this.selectedEvent.some((event) => event.eventLabel === 'Leads')) {
        opts = opts.filter(
          (item) =>
            ![
              'leadTimeToConvert',
              'leadTotalExposures',
              // 'lead',
              'leadConversionRate',
              'leadCpa',
              'leadPm',
            ].includes(item.key)
        );

        this.selectedMetricsOptions = this.selectedMetricsOptions.filter(
          (item) =>
            ![
              'leadTimeToConvert',
              'leadTotalExposures',
              'leadConversionRate',
              'leadCpa',
              'leadPm',
            ].includes(item)
        );
      }

      if (!this.selectedEvent.some((event) => event.eventLabel === 'Checkouts')) {
        opts = opts.filter(
          (item) =>
            ![
              'checkoutTimeToConvert',
              'checkoutTotalExposures',
              'checkoutConversionRate',
              'checkoutCpa',
              'checkoutPm',
            ].includes(item.key)
        );
        this.selectedMetricsOptions = this.selectedMetricsOptions.filter(
          (item) =>
            ![
              'checkoutTimeToConvert',
              'checkoutTotalExposures',
              'checkoutConversionRate',
              'checkoutCpa',
              'checkoutPm',
            ].includes(item)
        );
      }

      if (!this.selectedEvent.some((event) => event.eventLabel === 'Page Visits')) {
        opts = opts.filter(
          (item) =>
            ![
              'pageVisitTimeToConvert',
              'pageVisitTotalExposures',
              'pageVisitConversionRate',
              'pageVisitCpa',
              'pageVisitPm',
            ].includes(item.key)
        );
        this.selectedMetricsOptions = this.selectedMetricsOptions.filter(
          (item) =>
            ![
              'pageVisitTimeToConvert',
              'pageVisitTotalExposures',
              'pageVisitConversionRate',
              'pageVisitCpa',
              'pageVisitPm',
            ].includes(item)
        );
      }

      if (!this.selectedEvent.some((event) => event.eventLabel === 'Revenue')) {
        opts = opts.filter((item) => !['revenue', 'roas'].includes(item.key));

        this.selectedMetricsOptions = this.selectedMetricsOptions.filter(
          (item) => !['revenue', 'roas'].includes(item)
        );
      }

      if (!this.selectedEvent.some((event) => event.eventLabel === 'Location Visits')) {
        opts = opts.filter(
          (item) =>
            ![
              'locationVisitTimeToConvert',
              'locationVisitTotalExposures',
              'locationVisitConversionRate',
              'locationVisitCpa',
              'locationVisitPm',
            ].includes(item.key)
        );
        this.selectedMetricsOptions = this.selectedMetricsOptions.filter(
          (item) =>
            ![
              'locationVisitTimeToConvert',
              'locationVisitTotalExposures',
              'locationVisitConversionRate',
              'locationVisitCpa',
              'locationVisitPm',
            ].includes(item)
        );
      }

      if (!this.selectedEvent.some((event) => event.eventLabel === 'Activities')) {
        opts = opts.filter(
          (item) =>
            ![
              'activityTimeToConvert',
              'activityTotalExposures',
              'activityConversionRate',
              'activityCpa',
              'activityPm',
            ].includes(item.key)
        );
        this.selectedMetricsOptions = this.selectedMetricsOptions.filter(
          (item) =>
            ![
              'activityTimeToConvert',
              'activityTotalExposures',
              'activityConversionRate',
              'activityCpa',
              'activityPm',
            ].includes(item)
        );
      }

      if (this.hideCostMetrics) {
        // hide from the list
        opts = opts.reduce((acc, item) => {
          // Check if the item should be hidden
          const shouldHide = COST_METRICS_KEYS_TO_HIDE.some((someKey) =>
            item.key.toLowerCase().includes(someKey)
          );

          if (shouldHide) {
            // Set checked to false for the hidden item
            item.checked = false;
          } else {
            // Only add to the accumulator if the item is not hidden
            acc.push(item);
          }

          return acc;
        }, []);
      }
      // Assign the modified array back to reportMetricsOptions
      this.reportMetricsOptions = opts;
    },
    setMetricOpts(isMounted = false) {
      if (this.selectedExportTableRow) {
        this.selectedReportKind = this.selectedExportTableRow;
      }
      const key = `${this.selectedReportType}`;
      let opts = JSON.parse(JSON.stringify(REPORTS_NEW_BUILDER_METRIC_OPTIONS[key] || []));
      opts = opts.filter((opt) => opt.key !== 'incrementalReach');
      if (this.selectedEvent.isFalseEvent) {
        opts = opts.filter((opt) => !['checkouts', 'metric', 'pm'].includes(opt.key));
      }
      const disabledOptions = REPORT_BUILDER_DISABLED_METRICS[this.selectedReportKind] || [];
      const isAllEvnOptSelected = this.reportMetricsOptions.filter((op) => op.key === 'ep')?.[0]
        ?.checked;
      if (isAllEvnOptSelected && key !== 'rollup') {
        this.allEventPerformance = true;
        return;
      }
      opts = opts.map((item) => {
        if (item.key === 'videoCompletionRate') item.value = `(%)Completion Rate`;
        if (item.key === 'videoCompletions') item.value = `Completions`;
        return item;
      });
      opts.forEach((opt) => {
        const isOptionDisabled = disabledOptions.includes(opt.key);
        opt.checked = !isOptionDisabled;
        opt.disabled = isOptionDisabled;

        if (this.selectedExportTableRow) {
          if (['reach', 'frequency'].includes(opt.key)) {
            opt.checked = false;
          } else if (this.selectedEvent.isFalseEvent) {
            if (['videoCompletions', 'videoCompletionRate'].includes(opt.key)) {
              opt.checked = this.selectedEvent.isVcrEvent;
            }
            if (['clicks', 'clickThroughRate'].includes(opt.key)) {
              opt.checked = this.selectedEvent.isCtrEvent;
            }
          } else if (
            ['videoCompletions', 'videoCompletionRate', 'clicks', 'clickThroughRate'].includes(
              opt.key
            )
          ) {
            opt.checked = false;
          }
        }
        if (key === 'performance' && opt.key === 'checkouts') {
          opt.key = this.selectedEvent.isRevenueEvent ? 'revenue' : 'checkouts';
          opt.name = this.selectedEvent.isRevenueEvent ? '($)Rev' : this.selectedEventLabel;
          opt.value = this.selectedEventLabel;
          opt.formatter.format = this.selectedEvent.isRevenueEvent ? '0,0.00' : '0,0.[00]';
        } else if (key === 'performance' && opt.key === 'metric') {
          opt.checked =
            isMounted ||
            this.reportMetricsOptions.filter((oa) => oa.key === 'cpa' || oa.key === 'roas')?.[0]
              ?.checked;
          if (this.selectedEventLabel === 'Incremental Reach') {
            opt.key = 'incrementalReachPercentage';
            opt.name = 'Inc. Reach %';
            opt.formatter.format = '0';
          } else {
            const unit = this.selectedEvent.isRevenueEvent ? '%' : '$';
            opt.key = this.selectedEvent.isRevenueEvent ? 'roas' : 'cpa';
            opt.name = `(${unit})${this.selectedEvent.metric}`;
            opt.formatter.format = this.selectedEvent.isRevenueEvent ? '0' : '0,0.00';
          }
          opt.infinityKey = `${opt.key}Infinity`;
          opt.value = this.selectedEvent.metric;
        }
        if (opt.key === 'ep') {
          opt.checked = false;
        }
      });
      opts = opts.filter((opt) => opt.key !== 'incrementalReachPercentage');
      if (this.selectedEventLabel === 'Incremental Reach') {
        opts.forEach((op) => {
          if (op.key === 'incrementalReachPercentage') {
            op.value = 'Inc. Reach %';
            op.checked = true;
          }
          return op;
        });
      }

      this.reportMetricsOptions = opts.filter(
        (opt) => opt.value && opt.name !== 'Incremental Reach'
      );

      if (!isMounted) {
        this.setMetricOptsForEvents(this.reportMetricsOptions);
      }
    },
    onSelectMethodology(method) {
      this.filters.methodology = method;
      if (!this.isMniAccount) {
        this.handleFilterChangeOnExistingTemplate();
      }
    },
    handleFilterChangeOnExistingTemplate() {
      if (this.initialFilterChange || this.isRestoringSession) return;
      if (!PRE_DEFINED_TEMPLATES_WITH_NEW.includes(this.filters.template)) {
        if (
          this.selectedTemplateType &&
          PRE_DEFINED_TEMPLATES.includes(this.selectedTemplateType)
        ) {
          this.filters.template = this.selectedTemplateType;
          this.$store.set('dashboard/filters@template', this.selectedTemplateType);
          this.reportName = '';
          this.selectedTemplateId = '';
          this.saveTemplate = true;
          this.selectedTemplateType = '';
          if (this.scheduleReport) {
            this.disableSaveReportBasedOnSchedReport = true;
          }
        } else {
          this.filters.template = 'new';
          this.$store.set('dashboard/filters@template', 'new');
          this.reportName = '';
          this.selectedTemplateId = '';
          this.saveTemplate = true;
          if (this.scheduleReport) {
            this.disableSaveReportBasedOnSchedReport = true;
          }
        }
      }
    },
    bDatepickerChanges(data) {
      if (this.isRestoringSession) return;

      this.handleFilterChangeOnExistingTemplate();

      const datePickerPayload = {
        startDate: data.dates[0],
        endDate: data.dates[1],
        dateRangeOption: data.dateRangeOption,
      };

      this.checkDateForScheduler(datePickerPayload.dateRangeOption);

      this.$store.set('dashboard/dates', datePickerPayload);
      this.$store.dispatch('common/setAppliedFilterCount', this.appliedFilterCountNew);
      this.$forceUpdate();
    },
    getSelectedFilters(filters, filterOptionsArray) {
      const selectFilterVals = {};
      filterOptionsArray?.forEach((fl) => {
        selectFilterVals[fl] = filters[fl]?.filter((f) => f.checked) || [];
      });
      return JSON.parse(JSON.stringify(selectFilterVals));
    },
    handleRunNowChange(val) {
      this.runNow = val;
    },
    handleSaveTemplateChange(val) {
      this.saveTemplate = val;
      this.handleFilterChangeOnExistingTemplate();
    },
    handleScheduleReportChange(val) {
      this.scheduleReport = val;
      if (val) {
        this.disableSaveReportBasedOnSchedReport = true;
      } else {
        this.disableSaveReportBasedOnSchedReport = false;
      }
      this.handleFilterChangeOnExistingTemplate();
    },
    handleExportNSendReportChange(val) {
      this.exportNSendReport = val;
    },
    handleFrequencyChange(val) {
      this.selectedFrequency = val;
      this.handleFilterChangeOnExistingTemplate();
    },
    handleEmailChange(val) {
      this.emailsToSched = val;
      this.handleFilterChangeOnExistingTemplate();
    },
    handleEmailToSendChange(val) {
      this.emailsToSend = val;
    },
    handleScheduleStartDateChange(val) {
      this.scheduleStartDate = val;
      this.handleFilterChangeOnExistingTemplate();
    },
    handleScheduleEndDateChange(val) {
      this.scheduleEndDate = val;
      this.handleFilterChangeOnExistingTemplate();
    },
    trimReportName(reportName) {
      this.reportName = reportName?.trim() || ''; // Trim leading and trailing spaces
      if (!PRE_DEFINED_TEMPLATES.includes(this.filters.template)) {
        this.filters.template = 'new';
        this.$store.set('dashboard/filters@template', 'new');
      }
      this.selectedTemplateId = '';
    },
    async applyTemplateFilters(templateFilters) {
      this.selectedDimensionsOptions = templateFilters.dimensions;
      if (this.selectedDimensionsOptions.includes('publisher')) {
        this.selectedReportKind = 'publisher';
      } else if (this.selectedDimensionsOptions.includes('overall')) {
        this.selectedReportKind = 'overall';
      }
      this.setMetricOpts();

      this.eventOptions.forEach((event) => {
        const matchedEvent = templateFilters.reportMetrics.find((item) => {
          const formattedMetric = this.formatEventNamesReverse(item.metrics);
          if (
            (item.metrics === 'completions' || item.metrics === 'clicks') &&
            formattedMetric === event.event
          ) {
            // Allow item.event to be false for these specific cases
            return true;
          }

          // Default logic for other cases
          return formattedMetric === event.event && item.event === true;
        });

        if (matchedEvent) {
          event.selected = true;
          if (event.children && event.children.length > 0) {
            event.children.forEach((subCategory) => {
              const matchedSubCategory = matchedEvent.subCategory.find(
                (sub) => sub === subCategory.category
              );
              subCategory.selected = !!matchedSubCategory;
            });
          }
        } else {
          event.selected = false;
          if (event.children && event.children.length > 0) {
            event.children.forEach((subCategory) => {
              subCategory.selected = false;
            });
          }
        }
      });

      this.selectedMetricsOptions = templateFilters.reportMetrics
        .filter((item) => item.event === false)
        .map((item) => item.metrics);

      if (this.selectedMetricsOptions.length === 1 && this.selectedMetricsOptions[0] === 'ep') {
        this.handleMetricsChange('ep');
      }

      if (
        this.selectedDimensionsOptions.length === 1 &&
        PRE_DEFINED_TEMPLATES_WITH_NO_AUDIENCE_INSIGHTS.includes(this.selectedDimensionsOptions[0])
      ) {
        [this.selectedTemplateType] = this.selectedDimensionsOptions;
        // disable dimensions
        this.selectedDimensionsOptions = [];
        this.disableAllDimensions = true;
        // disable metrics
        this.selectedMetricsOptions = [];
        this.selectAllMetrics = false;
        this.disabledAllMetrics = true;
        this.disabledAllEvents = false;
        this.singleSelectMode = true;
        this.disableInterval = true;
      }

      if (
        templateFilters.presetTemplateName &&
        templateFilters.presetTemplateName === FOOT_TRAFFIC_SUMMARY_REPORT_TEMPLATE_NAME
      ) {
        // disable dimensions
        this.selectedDimensionsOptions = [];
        this.disableAllDimensions = true;
        // disable metrics
        this.selectedMetricsOptions = [];
        this.selectAllMetrics = false;
        this.disabledAllMetrics = true;
        this.disabledAllEvents = false;
        this.singleSelectMode = true;
        this.disableInterval = true;
        this.selectedTemplateType = FOOT_TRAFFIC_SUMMARY_REPORT;
      }

      if (
        templateFilters.presetTemplateName &&
        (templateFilters.presetTemplateName === INSIGHTS_CMPGN_DELIV_PRESET_TEMPLATE_NAME ||
          templateFilters.presetTemplateName === INSIGHTS_CONV_EVNT_PRESET_TEMPLATE_NAME)
      ) {
        this.disableAllDimensions = true;
        // disable metrics
        this.selectedMetricsOptions = [];
        this.selectAllMetrics = false;
        this.disabledAllMetrics = true;
        this.singleSelectMode = true;
        this.disableInterval = true;

        this.disabledAllEvents = true;
        if (templateFilters.presetTemplateName === INSIGHTS_CMPGN_DELIV_PRESET_TEMPLATE_NAME) {
          this.disableLookBackWindow = true;
          this.selectedTemplateType = AUDIENCE_INSIGHTS_CAMPAIGNS_REPORT;
        }
        if (templateFilters.presetTemplateName === INSIGHTS_CONV_EVNT_PRESET_TEMPLATE_NAME) {
          this.disabledAdvanceFilter = true;
          this.selectedTemplateType = AUDIENCE_INSIGHTS_CONVERSION_REPORT;
        }
      }

      if (
        templateFilters.presetTemplateName &&
        templateFilters.presetTemplateName === CAT_SUBCAT_REPORT_TEMPLATE_NAME
      ) {
        this.disableAllDimensions = false;
        this.selectedMetricsOptions = [];
        this.selectAllMetrics = false;
        this.disabledAllMetrics = true;
        this.singleSelectMode = false;
        this.disableInterval = false;
        this.selectedTemplateType = CAT_SUBCAT_REPORT;
      }

      if (
        this.reportMetricsOptions.length ===
        templateFilters.reportMetrics.filter((item) => item.event === false).length + 1
      ) {
        this.selectAllMetrics = true;
        this.toggleSelectAllMetrics();
      }
      this.setMetricOptsForEvents(this.reportMetricsOptions);

      const initFilters = this.isMniAccount ? this.initialFilters : this.getStoredDefaultFilters();
      let campaignsList = [];
      if (this.isMniAccount) {
        const campaignOpts = this.filters.campaignOptions || initFilters?.campaignOptions;
        campaignOpts.forEach((campaign) => {
          campaign.checked = templateFilters.campaignIds?.includes(String(campaign.campaignId));
        });
        this.$store.set('dashboard/filters@campaignOptions', campaignOpts);
        campaignsList = campaignOpts;
        this.selectedFilters.campaignOptions = campaignOpts.filter((c) => c.checked);
      } else {
        const ioOpts = this.filters.ioOptions || initFilters?.ioOptions;
        ioOpts.forEach((campaign) => {
          campaign.checked = templateFilters.campaignIds?.includes(String(campaign.campaignId));
        });
        this.filters.ioOptions = ioOpts;
        this.$store.set(
          'dashboard/filters@ioOptions',
          ioOpts.map((option) => ({ ...option, disabled: false }))
        );
        campaignsList = ioOpts;

        this.selectedFilters.ioOptions = ioOpts.filter((io) => io.checked);

        this.isInitialCampaignChange = true;
        this.isInitialAdGChange = true;
        await this.fetchAdOptionsFromCampaigns(templateFilters.adGroups);
      }

      if (!this.isMniAccount) {
        const methodologyOptions = this.$store.copy('dashboard/filters@methodologyOptions');
        let methodologyOption = {};

        methodologyOption = methodologyOptions.find((mo) => {
          return mo.id.toUpperCase() === templateFilters.methodology?.toUpperCase();
        });
        const methodologyOptionId = methodologyOption?.id;
        this.onSelectMethodology(methodologyOptionId || DEFAULT_METHODOLOGY);
      }

      if (this.isMniAccount) {
        EventBus.isAdvanceFilterLoaded = false;
        this.advanceFilterLoading = true;
        EventBus.$emit('call-get-advance-filter-data', 'campaignOptions', campaignsList);
      }

      this.filters.conversionWindowDisplay = templateFilters.conversionWindow;
      this.filters.conversionWindowUnit = templateFilters.conversionWindowUnit;
      this.filters.interval = templateFilters.interval.toLowerCase();

      if (!this.isMniAccount) {
        const totalOrUniqueRadioOptions = this.$store.copy(
          'dashboard/filters@totalOrUniqueRadioOptions'
        );
        if (templateFilters.showUniques) {
          totalOrUniqueRadioOptions[0].active = false;
          totalOrUniqueRadioOptions[1].active = true;
        } else {
          totalOrUniqueRadioOptions[0].active = true;
          totalOrUniqueRadioOptions[1].active = false;
        }
        this.updateTotalOrUniqueRadioOptions(totalOrUniqueRadioOptions);
      }

      let datePickerPayload = {};
      switch (templateFilters.dateRangeType) {
        case 'LAST_7_DAYS':
          datePickerPayload = {
            startDate: convertEpochToNYTimezone(offsetDate(8)),
            endDate: convertEpochToNYTimezone(offsetDate(2)),
            dateRangeOption: '6',
          };
          break;
        case 'LAST_2_WEEKS':
          datePickerPayload = {
            startDate: convertEpochToNYTimezone(offsetDate(15)),
            endDate: convertEpochToNYTimezone(offsetDate(2)),
            dateRangeOption: '14',
          };
          break;
        case 'LAST_30_DAYS':
          datePickerPayload = {
            startDate: convertEpochToNYTimezone(offsetDate(31)),
            endDate: convertEpochToNYTimezone(offsetDate(2)),
            dateRangeOption: '7',
          };
          break;
        case 'CAMPAIGN_TO_DATE': {
          const dateRange = getCampaignDateRange(campaignsList, true);
          datePickerPayload = {
            startDate: convertEpochToNYTimezone(dateRange.startDate),
            endDate: convertEpochToNYTimezone(dateRange.endDate),
            dateRangeOption: '13',
          };
          break;
        }
        case 'CUSTOM':
          datePickerPayload = {
            startDate: moment(templateFilters.startDate),
            endDate: moment(templateFilters.endDate),
            dateRangeOption: '0',
          };
          break;
        default:
          break;
      }

      this.$store.set('dashboard/dates', datePickerPayload);
      this.dateKey += 1;

      if (this.isMniAccount) {
        await this.applyAdvanceFilterValues(templateFilters);
      } else {
        await this.callGetAFData(templateFilters);
      }

      this.checkDateForScheduler(datePickerPayload.dateRangeOption);
      this.$store.dispatch('common/setAppliedFilterCount', this.appliedFilterCountNew);
    },
    applyAdvanceFilterValues(templateFilters) {
      const savedCreatives = (templateFilters.creatives || []).map(String);
      const savedMediaTypes = (templateFilters.mediaTypes || []).map(String);
      const savedAudiences = (templateFilters.audiences || []).map(String);
      return new Promise((resolve) => {
        const apply = () => {
          this.advanceFilterLoading = true;
          setTimeout(() => {
            const creativeOpts = this.creativeOptions || this.initialFilters?.creativeOptions;
            creativeOpts.forEach((creatives) => {
              creatives.checked = savedCreatives.includes(String(creatives.key));
            });
            this.filters.creativeOptions = creativeOpts;
            this.$store.set('dashboard/filters@creativeOptions', creativeOpts);

            const mediaTypeOpts = this.mediaTypeOptions || this.initialFilters?.mediaTypeOpts;
            mediaTypeOpts.forEach((media) => {
              media.checked = savedMediaTypes.includes(String(media.value));
            });
            this.filters.mediaTypeOptions = mediaTypeOpts;
            this.$store.set('dashboard/filters@mediaTypeOptions', mediaTypeOpts);

            const audienceOpts = this.audienceOptions || this.initialFilters?.audienceOpts;
            audienceOpts.forEach((audience) => {
              audience.checked = savedAudiences.includes(String(audience.value));
            });
            this.filters.audienceOptions = audienceOpts;
            this.$store.set('dashboard/filters@audienceOptions', audienceOpts);
            this.advanceFilterLoading = false;
            resolve();
          }, 500);
        };
        const wait = () => {
          if (EventBus.isAdvanceFilterLoaded) {
            apply();
          } else {
            setTimeout(wait, 200);
          }
        };
        wait();
      });
    },
    callGetAFData(templateFilters) {
      return new Promise((resolve, reject) => {
        const run = async () => {
          this.$emit('update-data-loading', true);
          try {
            await this.getAdvanceFilterDataForNewReportBuilder(this.filters, templateFilters);
            resolve();
          } catch (error) {
            reject(error);
          } finally {
            this.$emit('update-data-loading', false);
          }
        };
        const wait = () => {
          if (this.adGroupsLoaded) {
            run();
          } else {
            setTimeout(wait, 200);
          }
        };
        wait();
      });
    },
    formatEventNames(name) {
      const eventMap = {
        Leads: 'lead',
        Checkouts: 'checkout',
        Revenue: 'revenue',
        'Page Visits': 'pageVisit',
        'Video Completions': 'completions',
        Clicks: 'clicks',
        'Location Visits': 'locationVisit',
        Activities: 'activity',
      };
      return eventMap[name] || name;
    },
    formatEventNamesReverse(name) {
      const eventMap = {
        lead: 'Leads',
        checkout: 'Checkouts',
        revenue: 'Revenue',
        pageVisit: 'Page Visits',
        completions: 'Video Completions',
        clicks: 'Clicks',
        locationVisit: 'Location Visits',
        activity: 'Activities',
      };
      return eventMap[name] || name;
    },
    exportNSendAllEp(payload, showIndicator = true) {
      this.disabledRunBtn = true;

      if (showIndicator) {
        this.sendingEmail = true;
      } else {
        this.sendingEmail = false;
      }

      this.$emit('export-n-send-all-ep-reports', payload);
    },
    async exportNSendTouchReports(payload, showIndicator = true) {
      this.disabledRunBtn = true;
      if (showIndicator) {
        this.sendingEmail = true;
      } else {
        this.sendingEmail = false;
      }

      this.$emit('export-n-send-touch-reports', payload);
    },
    async exportNSend(payload, showIndicator = true, isAudienceInsights = false) {
      this.disabledRunBtn = true;

      if (showIndicator) {
        this.sendingEmail = true;
      } else {
        this.sendingEmail = false;
      }

      payload.emailTo = this.emailsToSend.map((email) => email?.trim()).join(',');
      payload.email = true;

      const api = !isAudienceInsights
        ? reportBuilderV2API.runReport
        : reportBuilderV2API.runPresetReport;
      const opts = {
        headers: {
          'Content-Type': 'application/json',
        },
        respondHeaders: true,
      };
      api(payload.advertiserId, payload, opts)
        .then((response) => {
          if (response?.status === 200) {
            if (showIndicator) {
              if (response?.result?.firstAttemptFailed) {
                this.showSuccessMessage(FIRST_DAG_SUBMISSION_FAILURE_MESSAGE);
              } else {
                this.showSuccessMessage(MESSAGES.REPORT_EMAIL_SUCCESS(payload.emailTo));
              }
              this.emailSent = true;
            }
          } else {
            // Handle other status codes if needed.
            console.error('Unexpected status code:', response?.status);
            this.sendingEmail = false;
            this.emailSent = false;
            this.disabledRunBtn = false;
            if (showIndicator) {
              this.showErrorMessage('Unexpected server response');
            }
          }
        })
        .catch((error) => {
          const errorMessage =
            error.response?.data?.message ||
            'Due to an internal error, the email could not be sent. Please try again later.';
          console.error('Error:', errorMessage);
          this.sendingEmail = false;
          this.emailSent = false;
          this.disabledRunBtn = false;
          if (showIndicator) {
            this.showErrorMessage(errorMessage);
          }
        })
        .finally(() => {
          // Any code to run regardless of success or failure
          setTimeout(() => {
            this.disabledRunBtn = false;
            this.sendingEmail = false;
            this.emailSent = false;
          }, 2000);
        });
    },
    getDimensionPayload() {
      if (CAT_SUBCAT_REPORT === this.filters.template) {
        return this.selectedDimensionsOptions;
      }
      if (PRE_DEFINED_TEMPLATES_AUDIENCE_INSIGHTS.includes(this.filters.template)) {
        return [];
      }
      return PRE_DEFINED_TEMPLATES.includes(this.filters.template)
        ? [this.filters.template]
        : this.selectedDimensionsOptions;
    },
    getMediaTypesPayload() {
      if (CAT_SUBCAT_REPORT === this.filters.template) {
        return this.selectedMediaTypes;
      }
      if (PRE_DEFINED_TEMPLATES_AUDIENCE_INSIGHTS.includes(this.filters.template)) {
        return [];
      }
      return this.selectedMediaTypes;
    },
    runOrSaveReport() {
      setTimeout(() => {
        if (!this.saveTemplate && ['new'].includes(this.filters.template)) {
          this.reportName = '';
          this.selectedTemplateId = '';
        }

        // validate
        if (this.exportNSendReport && this.emailsToSend.length === 0) {
          const errorMessage = 'Please enter email/s to send the report.';
          this.showErrorMessage(errorMessage);

          this.showErrorOnEmailTo = true;
          this.animateError(this.$refs.emailToList); // Trigger the animation
          setTimeout(() => {
            this.showErrorOnEmailTo = false;
          }, 5000); // Hide after 5 seconds
          return;
        }

        if (
          !PRE_DEFINED_TEMPLATES.includes(this.filters.template) &&
          this.selectedDimensionsOptions.length < 1 &&
          this.selectedTemplateType.length !== 1 &&
          !PRE_DEFINED_TEMPLATES.includes(this.selectedTemplateType)
        ) {
          const errorMessage = 'Please select at least one dimension';
          this.showErrorMessage(errorMessage);

          this.showErrorOnDimension = true;
          this.animateError(this.$refs.dimensionsList); // Trigger the animation
          setTimeout(() => {
            this.showErrorOnDimension = false;
          }, 5000); // Hide after 5 seconds

          return;
        }

        const transformedReportMetrics = this.selectedMetricsOptions.map((metric) => ({
          metrics: metric,
          event: false,
          subCategory: [],
        }));
        const transformedEventOptions = this.eventOptions
          .filter((option) => option.selected)
          .map((option) => ({
            metrics: this.formatEventNames(option.event),
            event: !(option.event === 'Video Completions' || option.event === 'Clicks'),
            subCategory:
              option.children?.length > 0
                ? option.children.filter((child) => child.selected).map((child) => child.category)
                : [],
          }));
        const mergedMetrics = transformedReportMetrics.concat(transformedEventOptions);

        if (
          PRE_DEFINED_TEMPLATES_WITH_NO_AUDIENCE_INSIGHTS.includes(this.filters.template) &&
          mergedMetrics.length < 1
        ) {
          const errorMessage = 'Please select at least one event';
          this.showErrorMessage(errorMessage);
          return;
        }

        const dates = this.$store.get('dashboard/dates');
        let dateRangeType = null;
        if (String(dates.dateRangeOption) === '6') {
          dateRangeType = 'LAST_7_DAYS';
        } else if (String(dates.dateRangeOption) === '14') {
          dateRangeType = 'LAST_2_WEEKS';
        } else if (String(dates.dateRangeOption) === '7') {
          dateRangeType = 'LAST_30_DAYS';
        } else if (String(dates.dateRangeOption) === '13') {
          dateRangeType = 'CAMPAIGN_TO_DATE';
        } else if (String(dates.dateRangeOption) === '0') {
          dateRangeType = 'CUSTOM';
        }

        const data = this.isMniAccount ? this.filters.campaignOptions : this.filters.ioOptions;
        const result = data.filter((f) => f?.checked);
        const campaignIds = result?.map((res) => res.key) || [];

        if (!campaignIds || campaignIds.length === 0) {
          const errorMessage = 'Please select at least one campaign';
          this.showErrorMessage(errorMessage);

          this.showErrorOnCampaign = true;
          this.animateError(this.$refs.campaignList); // Trigger the animation
          setTimeout(() => {
            this.showErrorOnCampaign = false;
          }, 5000); // Hide after 5 seconds

          return;
        }

        if (
          !this.isMniAccount &&
          Array.isArray(this.filters?.adGroupOptions) &&
          this.filters?.adGroupOptions.length > 0
        ) {
          // update ad group options only if it has data
          this.$store.set('dashboard/filters@adGroupOptions', this.filters?.adGroupOptions);
        }

        let adGroupsValue = null; // Default to null

        if (!this.isMniAccount) {
          // If it's NOT an MNI account
          if (this.selectedAdGrpOptStr && this.selectedAdGrpOptStr.length > 0) {
            // Check if the string is not empty
            adGroupsValue = this.selectedAdGrpOptStr.split(',').map((adG) => adG?.trim());
          }
        }

        const payload = {
          accountId: this?.account?.id,
          advertiserId: this.advertiser?.id,
          filters: {
            adGroups: adGroupsValue,
            audiences: this.selectedAudiences,
            campaignIds,
            conversionWindow: this.filters.conversionWindowDisplay,
            conversionWindowUnit: this.filters.conversionWindowUnit,
            creatives: this.selectedCreatives,
            dateRangeType,
            dimensions: this.getDimensionPayload(),
            endDate: this.getEndDate(dates),
            startDate: this.getStartDate(dates),
            interval: this.filters.interval?.toUpperCase(),
            keywords: null,
            mediaTypes: this.getMediaTypesPayload(),
            methodology: this.filters.methodology?.toUpperCase(),
            reportMetrics: mergedMetrics,
            pixelIds: null,
            publisher: this.selectedPublishers,
            showUniques: this.filters.showUniques,
          },
          name: this.reportName.trim(),
        };

        if (this.scheduleReport) {
          let errorMessage = '';

          if (
            !this.selectedFrequency ||
            this.selectedFrequency === 'select' ||
            this.selectedFrequency.length === 0
          ) {
            errorMessage += ' Frequency,';
          }

          if (!this.scheduleStartDate || this.scheduleStartDate.length === 0) {
            errorMessage += ' Schedule Start Date,';
          }

          if (!this.scheduleEndDate || this.scheduleEndDate.length === 0) {
            errorMessage += ' Schedule End Date,';
          }

          if (!this.emailsToSched || this.emailsToSched.length === 0) {
            errorMessage += ' Email To,';
          }

          if (errorMessage.length > 1) {
            let err = 'Please set values for these fields:';
            err += errorMessage;
            this.showErrorMessage(err?.replace(/,$/, '.'));

            this.showErrorOnSchedule = true;
            this.animateError(this.$refs.schedulerList); // Trigger the animation
            setTimeout(() => {
              this.showErrorOnSchedule = false;
            }, 5000); // Hide after 5 seconds
            return;
          }
          payload.schedule = this.selectedFrequency;
          payload.scheduleStatus = 'ACTIVE';
          payload.scheduleStartDate = this.scheduleStartDate;
          payload.scheduleEndDate = this.scheduleEndDate;
          payload.emailTo = this.emailsToSched
            .map((email) => email?.trim()) // Trim spaces from each email
            .join(',');
        }

        if ((this.saveTemplate || this.scheduleReport) && !this.reportName) {
          this.showErrorMessage(ERROR_MESSAGE_NAME_REQUIRED);

          this.showErrorOnTemplateName = true;
          this.animateError(this.$refs.templateNameVal); // Trigger the animation
          setTimeout(() => {
            this.showErrorOnTemplateName = false;
          }, 5000); // Hide after 5 seconds

          return;
        }

        if (
          PRE_DEFINED_TEMPLATES.includes(this.filters.template) &&
          !this.runNow &&
          !this.exportNSendReport &&
          !this.saveTemplate
        ) {
          if (this.scheduleReport) {
            this.showErrorMessage(ERROR_MESSAGE_SCHEDULED_PRESET_REPORT);
          } else {
            this.showErrorMessage(ERROR_MESSAGE_DELIVERY_SELECTION);

            this.showErrorOnDeliveryVals = true;
            this.animateError(this.$refs.deliveryVals); // Trigger the animation
            setTimeout(() => {
              this.showErrorOnDeliveryVals = false;
            }, 5000); // Hide after 5 seconds
          }

          return;
        }

        // prepare payload for audience insights
        const { template } = this.filters;
        if (
          AUDIENCE_INSIGHTS_CAMPAIGNS_REPORT === template ||
          AUDIENCE_INSIGHTS_CAMPAIGNS_REPORT === this.selectedTemplateType
        ) {
          payload.filters.presetTemplateName = INSIGHTS_CMPGN_DELIV_PRESET_TEMPLATE_NAME;
          this.runNow = false;
        } else if (
          AUDIENCE_INSIGHTS_CONVERSION_REPORT === template ||
          AUDIENCE_INSIGHTS_CONVERSION_REPORT === this.selectedTemplateType
        ) {
          payload.filters.presetTemplateName = INSIGHTS_CONV_EVNT_PRESET_TEMPLATE_NAME;
          this.runNow = false;
        } else if (
          CAT_SUBCAT_REPORT === template ||
          CAT_SUBCAT_REPORT === this.selectedTemplateType
        ) {
          payload.filters.presetTemplateName = CAT_SUBCAT_REPORT_TEMPLATE_NAME;
          this.runNow = false;
        } else if (
          FOOT_TRAFFIC_SUMMARY_REPORT === template ||
          FOOT_TRAFFIC_SUMMARY_REPORT === this.selectedTemplateType
        ) {
          payload.filters.presetTemplateName = FOOT_TRAFFIC_SUMMARY_REPORT_TEMPLATE_NAME;
          this.runNow = true;
        } else {
          payload.filters.presetTemplateName = null;
        }

        // make sure run now is set to false (except demo touch reports which support run now)
        if (
          PRE_DEFINED_TEMPLATES.includes(this.filters.template) ||
          PRE_DEFINED_TEMPLATES.includes(this.selectedTemplateType)
        ) {
          if (!this.isDemoTouchReport) {
            this.runNow = false;
          }
        }

        if (this.runNow || this.exportNSendReport) {
          const isMaxRunCountReach = verifyRunCount(RUN_REPORT_TIMESTAMPS_KEY);
          if (isMaxRunCountReach) {
            this.showErrorMessage(MAX_RUN_REPORT_COUNT_REACH_ERROR_MSG);
            return;
          }
        }
        if (payload.filters.reportMetrics.some((metric) => metric?.metrics === 'ep')) {
          if ((this.saveTemplate || this.scheduleReport) && this.filters.template === 'new') {
            // setting to true to fetch updated report list
            EventBus.fetchSavedReports = true;

            this.buttonLabel = 'RUNNING...';
            // Only new template can be save
            reportBuilderV2API
              .createReportTemplate(this.advertiser?.id, payload)
              .then((response) => {
                if (response) {
                  // success saving the template
                  if (this.runNow) {
                    if (this.exportNSendReport) {
                      payload.exportNSend = true;
                      payload.exportNSendEmails = this.emailsToSend
                        .map((email) => email?.trim())
                        .join(',');
                    }
                    // run now
                    EventBus.$emit('switch-to-tab-index', 2, payload.name);
                    const sessionFilter = payload.filters;
                    sessionFilter.selectedTemplate = this.filters.template;
                    this.saveRb2SessionFilters(sessionFilter);
                    this.triggerSearch(payload);
                  } else {
                    if (this.exportNSendReport) {
                      payload.emailList = this.emailsToSend.map((email) => email?.trim()).join(',');
                      const sessionFilter = payload.filters;
                      sessionFilter.selectedTemplate = this.filters.template;
                      this.saveRb2SessionFilters(sessionFilter);
                      this.exportNSendAllEp(payload, !this.saveTemplate);
                    }
                    // go to my reports
                    const sessionFilter = payload.filters;
                    sessionFilter.selectedTemplate = this.filters.template;
                    this.saveRb2SessionFilters(sessionFilter);
                    EventBus.$emit('switch-to-tab-index', 1);
                  }
                }
              })
              .catch((error) => {
                // do not run report if there is an issue with saving template
                // Extract the error message from the response
                const errorMessage = util.getErrorMessage(error, true);
                this.buttonLabel = 'RUN REPORT';
                // Handle the specific error or notify the user
                this.showErrorMessage(errorMessage);
              });
          } else if (
            this.runNow &&
            !(this.saveTemplate || this.scheduleReport) &&
            this.filters.template === 'new'
          ) {
            this.buttonLabel = 'RUNNING...';

            if (this.exportNSendReport) {
              payload.exportNSend = true;
              payload.exportNSendEmails = this.emailsToSend.map((email) => email?.trim()).join(',');
            }

            EventBus.$emit('switch-to-tab-index', 2, '');
            const sessionFilter = payload.filters;
            sessionFilter.selectedTemplate = this.filters.template;
            this.saveRb2SessionFilters(sessionFilter);
            this.triggerSearch(payload);
          } else if (
            (this.saveTemplate || this.scheduleReport) &&
            this.runNow &&
            this.filters.template !== 'new'
          ) {
            this.buttonLabel = 'RUNNING...';

            if (this.exportNSendReport) {
              payload.exportNSend = true;
              payload.exportNSendEmails = this.emailsToSend.map((email) => email?.trim()).join(',');
            }

            EventBus.$emit('switch-to-tab-index', 2, payload.name);
            const sessionFilter = payload.filters;
            sessionFilter.selectedTemplate = this.filters.template;
            this.saveRb2SessionFilters(sessionFilter);
            this.triggerSearch(payload);
          } else if (
            (this.saveTemplate || this.scheduleReport) &&
            !this.runNow &&
            !this.exportNSendReport &&
            this.filters.template !== 'new'
          ) {
            this.showErrorMessage('Please modify any filter before running.');
            return;
          } else if (!this.saveTemplate && this.runNow && this.filters.template !== 'new') {
            this.buttonLabel = 'RUNNING...';

            if (this.exportNSendReport) {
              payload.exportNSend = true;
              payload.exportNSendEmails = this.emailsToSend.map((email) => email?.trim()).join(',');
            }

            EventBus.$emit('switch-to-tab-index', 2, payload.name);
            const sessionFilter = payload.filters;
            sessionFilter.selectedTemplate = this.filters.template;
            this.saveRb2SessionFilters(sessionFilter);
            this.triggerSearch(payload);
          } else {
            if (this.exportNSendReport) {
              payload.emailList = this.emailsToSend.map((email) => email?.trim()).join(',');
              const sessionFilter = payload.filters;
              sessionFilter.selectedTemplate = this.filters.template;
              this.saveRb2SessionFilters(sessionFilter);
              this.exportNSendAllEp(payload, !this.saveTemplate);
              return;
            }
            if (this.scheduleReport) {
              this.showErrorMessage(ERROR_MESSAGE_SCHEDULED_PRESET_REPORT);
            } else {
              this.showErrorMessage(ERROR_MESSAGE_DELIVERY_SELECTION);

              this.showErrorOnDeliveryVals = true;
              this.animateError(this.$refs.deliveryVals); // Trigger the animation
              setTimeout(() => {
                this.showErrorOnDeliveryVals = false;
              }, 5000); // Hide after 5 seconds
            }

            return;
          }
        } else if (
          PRE_DEFINED_TEMPLATES_WITH_NO_AUDIENCE_INSIGHTS.includes(this.filters.template) ||
          (this.selectedTemplateType !== '' &&
            PRE_DEFINED_TEMPLATES_WITH_NO_AUDIENCE_INSIGHTS.includes(this.selectedTemplateType))
        ) {
          if (
            (this.saveTemplate || this.scheduleReport) &&
            (this.selectedTemplateType === '' || this.isDemoTouchReport)
          ) {
            this.buttonLabel = 'RUNNING...';

            // setting to true to fetch updated report list
            EventBus.fetchSavedReports = true;
            // Only new template can be save
            reportBuilderV2API
              .createReportTemplate(this.advertiser?.id, payload)
              .then((response) => {
                if (response) {
                  // success saving the template
                  if (this.exportNSendReport) {
                    payload.emailList = this.emailsToSend.map((email) => email?.trim()).join(',');
                    payload.template = this.filters.template;
                    const sessionFilter = payload.filters;
                    sessionFilter.selectedTemplate = this.filters.template;
                    this.saveRb2SessionFilters(sessionFilter);
                    this.exportNSendTouchReports(payload);
                  }
                  if (this.runNow) {
                    this.buttonLabel = 'RUNNING...';
                    if (this.exportNSendReport) {
                      payload.exportNSend = true;
                      payload.exportNSendEmails = this.emailsToSend
                        .map((email) => email?.trim())
                        .join(',');
                    }
                    EventBus.$emit('switch-to-tab-index', 2, payload.name);
                    payload.filters.template =
                      this.selectedTemplateType &&
                      PRE_DEFINED_TEMPLATES.includes(this.selectedTemplateType)
                        ? this.selectedTemplateType
                        : this.filters.template;
                    const sessionFilter = payload.filters;
                    sessionFilter.selectedTemplate = this.filters.template;
                    this.saveRb2SessionFilters(sessionFilter);
                    this.triggerSearch(payload);
                  } else {
                    // go to my reports
                    const sessionFilter = payload.filters;
                    sessionFilter.selectedTemplate = this.filters.template;
                    this.saveRb2SessionFilters(sessionFilter);
                    EventBus.$emit('switch-to-tab-index', 1);
                  }
                }
              })
              .catch((error) => {
                // do not run report if there is an issue with saving template
                // Extract the error message from the response
                const errorMessage = util.getErrorMessage(error, true);
                console.error('Error:', errorMessage);
                this.buttonLabel = 'RUN REPORT';
                // Handle the specific error or notify the user
                this.showErrorMessage(errorMessage);
              });
          } else if (this.runNow) {
            this.buttonLabel = 'RUNNING...';
            EventBus.$emit('switch-to-tab-index', 2, payload.name);
            if (
              this.selectedTemplateType !== '' &&
              PRE_DEFINED_TEMPLATES.includes(this.selectedTemplateType)
            ) {
              payload.filters.template = this.selectedTemplateType;
            } else {
              payload.filters.template = this.filters.template;
            }
            const sessionFilter = payload.filters;
            sessionFilter.selectedTemplate = this.filters.template;
            this.saveRb2SessionFilters(sessionFilter);
            this.triggerSearch(payload);
          } else if (this.exportNSendReport) {
            payload.emailList = this.emailsToSend.map((email) => email?.trim()).join(',');
            payload.template =
              this.selectedTemplateType !== '' &&
              PRE_DEFINED_TEMPLATES.includes(this.selectedTemplateType)
                ? this.selectedTemplateType
                : this.filters.template;
            const sessionFilter = payload.filters;
            sessionFilter.selectedTemplate = this.filters.template;
            this.saveRb2SessionFilters(sessionFilter);
            this.exportNSendTouchReports(payload, !this.saveTemplate);
            return;
          } else {
            if (this.scheduleReport) {
              this.showErrorMessage(ERROR_MESSAGE_SCHEDULED_PRESET_REPORT);
            } else {
              this.showErrorMessage('Please select additional option under Delivery.');
            }
            return;
          }
        } else if (
          (this.saveTemplate || this.scheduleReport) &&
          (this.filters.template === 'new' ||
            PRE_DEFINED_TEMPLATES_AUDIENCE_INSIGHTS.includes(this.filters.template))
        ) {
          // setting to true to fetch updated report list
          EventBus.fetchSavedReports = true;

          this.buttonLabel = 'RUNNING...';
          // Only new template can be save
          reportBuilderV2API
            .createReportTemplate(this.advertiser?.id, payload)
            .then((response) => {
              if (response) {
                // success saving the template
                if (this.runNow) {
                  if (this.exportNSendReport) {
                    payload.email = true;
                    payload.emailTo = this.emailsToSend.map((email) => email?.trim()).join(',');
                    payload.runNow = true;
                  }
                  // run now
                  EventBus.$emit('switch-to-tab-index', 2, payload.name);
                  const sessionFilter = payload.filters;
                  sessionFilter.selectedTemplate = this.filters.template;
                  this.saveRb2SessionFilters(sessionFilter);
                  this.triggerReportSearchWithoutID(payload);
                } else {
                  // go to my reports
                  const sessionFilter = payload.filters;
                  sessionFilter.selectedTemplate = this.filters.template;
                  this.saveRb2SessionFilters(sessionFilter);
                  EventBus.$emit('switch-to-tab-index', 1);
                }
              }
            })
            .catch((error) => {
              // do not run report if there is an issue with saving template
              // Extract the error message from the response
              const errorMessage = util.getErrorMessage(error, true);
              console.error('Error:', errorMessage);
              this.buttonLabel = 'RUN REPORT';
              // Handle the specific error or notify the user
              this.showErrorMessage(errorMessage);
            });
        } else if (this.runNow) {
          this.buttonLabel = 'RUNNING...';
          // run now
          let payLoad = {};
          if (this.selectedTemplateId && this.selectedTemplateId !== '') {
            payLoad = {
              id: this.selectedTemplateId,
              advertiserId: payload.advertiserId,
              name: payload.name,
              filters: payload.filters,
            };
          } else {
            payload.name = '';
            payLoad = payload;
          }

          if (this.exportNSendReport) {
            payload.email = true;
            payload.emailTo = this.emailsToSend.map((email) => email?.trim()).join(',');
            payload.runNow = true;
          }
          EventBus.$emit('switch-to-tab-index', 2, payload.name);
          // TODO TEST THIS
          const sessionFilter = payLoad.filters;
          sessionFilter.selectedTemplate = this.filters.template;
          this.saveRb2SessionFilters(sessionFilter);
          this.triggerReportSearch(payLoad);
        } else if (
          (this.saveTemplate || this.scheduleReport) &&
          !this.runNow &&
          !this.exportNSendReport &&
          this.filters.template !== 'new' &&
          !PRE_DEFINED_TEMPLATES_AUDIENCE_INSIGHTS.includes(this.selectedTemplateType)
        ) {
          this.showErrorMessage('Please select Run Now option.');

          this.showErrorOnRunNowOpts = true;
          this.animateError(this.$refs.runNowOpts); // Trigger the animation
          setTimeout(() => {
            this.showErrorOnRunNowOpts = false;
          }, 5000); // Hide after 5 seconds

          return;
        } else if (!this.exportNSendReport) {
          if (this.scheduleReport) {
            this.showErrorMessage(ERROR_MESSAGE_SCHEDULED_PRESET_REPORT);
          } else {
            this.showErrorMessage(ERROR_MESSAGE_DELIVERY_SELECTION);

            this.showErrorOnDeliveryVals = true;
            this.animateError(this.$refs.deliveryVals); // Trigger the animation
            setTimeout(() => {
              this.showErrorOnDeliveryVals = false;
            }, 5000); // Hide after 5 seconds
          }

          return;
        }

        if (
          !this.runNow &&
          !PRE_DEFINED_TEMPLATES.includes(this.filters.template) &&
          this.exportNSendReport &&
          !PRE_DEFINED_TEMPLATES_AUDIENCE_INSIGHTS.includes(this.selectedTemplateType) &&
          !payload.filters.reportMetrics.some((metric) => metric?.metrics === 'ep')
        ) {
          const sessionFilter = payload.filters;
          sessionFilter.selectedTemplate = this.filters.template;
          this.saveRb2SessionFilters(sessionFilter);
          this.exportNSend(payload, !this.saveTemplate);
        }

        if (
          (PRE_DEFINED_TEMPLATES_AUDIENCE_INSIGHTS.includes(this.filters.template) ||
            PRE_DEFINED_TEMPLATES_AUDIENCE_INSIGHTS.includes(this.selectedTemplateType)) &&
          this.exportNSendReport
        ) {
          const sessionFilter = payload.filters;
          sessionFilter.selectedTemplate = this.filters.template;
          this.saveRb2SessionFilters(sessionFilter);
          this.exportNSend(payload, !this.saveTemplate, true);
        }
      }, 100);
    },
    getEndDate(dates) {
      return formatDateForAPI(dates?.endDate);
    },
    getStartDate(dates) {
      return formatDateForAPI(dates?.startDate);
    },
    isEpSelected(reportMetrics) {
      return reportMetrics.some((metric) => metric?.metrics === 'ep');
    },
    triggerReportSearchWithoutID(payload) {
      if (EventBus.myReportsWithIDListenerReady) {
        // If the listener is ready, emit the event
        EventBus.$emit('do-new-report-search-from-my-reports-with-id', payload);
      } else {
        // If the listener isn't ready, retry after a short delay
        setTimeout(() => this.triggerReportSearchWithoutID(payload), 200); // Retry after 200ms
      }
    },
    triggerReportSearch(payload) {
      if (EventBus.myReportsListenerReady) {
        // If the listener is ready, emit the event
        EventBus.$emit('do-new-report-search-from-my-reports', payload);
      } else {
        // If the listener isn't ready, retry after a short delay
        setTimeout(() => this.triggerReportSearch(payload), 200); // Retry after 200ms
      }
    },
    triggerSearch(payload) {
      this.$nextTick(() => {
        if (EventBus.listenerReady) {
          // If the listener is ready, emit the event
          EventBus.$emit('do-new-report-search', payload);
        } else {
          // If the listener isn't ready, retry after a short delay
          setTimeout(this.triggerSearch(payload), 100); // Retry after 100ms
        }
      });
    },
    dropdownIOClosed() {
      if (this.isMniAccount) return;
      this.$emit('update-data-loading', true);
      this.getAdvanceFilterDataForNewReportBuilder(this.filters);
      this.$emit('update-data-loading', false);
    },
    dropdownAdGrpClosed() {
      if (this.isMniAccount) return;
      this.$emit('update-data-loading', true);
      this.getAdvanceFilterDataForNewReportBuilder(this.filters);
      this.$emit('update-data-loading', false);
    },
    async getAdvanceFilterDataForNewReportBuilder(filters, templateFilters = null) {
      this.$emit('update-data-loading', true);
      this.isAdvanceFilterLoading = true;
      let campaignIds = '';
      let adGroups = '';
      campaignIds = this.getCampaignIds(filters.ioOptions);
      adGroups = this.getSelectedAdGroupStr(filters.adGroupOptions);

      const dateRange = getCampaignDateRange(filters.ioOptions, true);

      this.filters.pixelOptions = [];
      this.filters.mediaTypeOptions = [];
      this.filters.audienceOptions = [];
      this.filters.creativeOptions = [];
      this.filters.keywordOptions = [];
      this.filters.publisherOptions = [];

      if (!dateRange.notAvailable) {
        try {
          const promises = [
            this.getConversionPixelOptions(campaignIds, adGroups, dateRange),
            this.getMediaTypes(campaignIds, adGroups, dateRange),
            this.getAudiences(campaignIds, adGroups, dateRange),
            this.getPublishers(),
            this.getCreatives(campaignIds, adGroups, dateRange),
            this.getKeywords(campaignIds, adGroups, dateRange),
          ];

          const results = await Promise.all(promises);

          [
            this.filters.pixelOptions,
            this.filters.mediaTypeOptions,
            this.filters.audienceOptions,
            this.filters.publisherOptions,
            this.filters.creativeOptions,
            this.filters.keywordOptions,
          ] = results;

          // After fresh API pull, apply the default selection behaviors unless applying a saved template
          if (!templateFilters) {
            if (this.filters.mediaTypeOptions) {
              this.filters.mediaTypeOptions = this.filters.mediaTypeOptions.map((m) => ({
                ...m,
                checked: true,
              }));
            }
            if (this.filters.creativeOptions) {
              this.filters.creativeOptions = this.filters.creativeOptions.map((c) => ({
                ...c,
                checked: false,
              }));
            }
            if (this.filters.audienceOptions) {
              this.filters.audienceOptions = this.filters.audienceOptions.map((a) => ({
                ...a,
                checked: false,
              }));
            }
            if (this.filters.publisherOptions) {
              this.filters.publisherOptions = this.filters.publisherOptions.map((p) => ({
                ...p,
                checked: false,
              }));
            }
          }

          // Push the populated arrays into the Vuex store to keep the UI in sync
          this.$store.set('dashboard/filters@mediaTypeOptions', this.filters.mediaTypeOptions);
          this.$store.set('dashboard/filters@creativeOptions', this.filters.creativeOptions);
          this.$store.set('dashboard/filters@audienceOptions', this.filters.audienceOptions);
          this.$store.set('dashboard/filters@publisherOptions', this.filters.publisherOptions);

          if (templateFilters) {
            this.setTemplateFilters(templateFilters);
          }
        } catch (error) {
          console.error('Error loading advanced filters:', error);
          this.isAdvanceFilterLoading = false;
          this.$emit('update-data-loading', false);
        } finally {
          this.isAdvanceFilterLoading = false;
          this.$emit('update-data-loading', false);
          this.$store.dispatch('common/setAppliedFilterCount', this.appliedFilterCountNew);
        }
      }
    },
    setTemplateFilters(templateFilters) {
      const savedCreatives = (templateFilters.creatives || []).map(String);
      const savedMediaTypes = (templateFilters.mediaTypes || []).map(String);
      const savedAudiences = (templateFilters.audiences || []).map(String);
      const savedPublishers = (templateFilters.publisher || []).map(String);

      const creativeOpts = [...this.filters.creativeOptions];
      creativeOpts.forEach((creatives) => {
        creatives.checked = savedCreatives.includes(String(creatives.key));
      });
      this.$store.set('dashboard/filters@creativeOptions', creativeOpts);

      const mediaTypeOpts = this.filters.mediaTypeOptions;
      mediaTypeOpts.forEach((media) => {
        media.checked = savedMediaTypes.includes(String(media.value));
      });
      this.$store.set('dashboard/filters@mediaTypeOptions', mediaTypeOpts);

      const audienceOpts = this.filters.audienceOptions;
      audienceOpts.forEach((audience) => {
        audience.checked = savedAudiences.includes(String(audience.value));
      });
      this.$store.set('dashboard/filters@audienceOptions', audienceOpts);

      const publisherOpts = this.filters.publisherOptions;
      publisherOpts.forEach((publisher) => {
        publisher.checked = savedPublishers.includes(String(publisher.key));
      });
      this.$store.set('dashboard/filters@publisherOptions', publisherOpts);
    },
    getCampaignIds(selectedData) {
      let result = '';
      let campaignIds;
      result = selectedData.filter((f) => f?.checked);
      if (result.length !== selectedData.length) {
        campaignIds =
          result
            ?.map((res) => {
              return res.key;
            })
            ?.filter((fres) => fres)
            ?.join(',') || '';
        return campaignIds;
      }
      return campaignIds;
    },
    getSelectedAdGroupStr(selectedData) {
      const adGroupOptions = selectedData;
      if (adGroupOptions.length === 0) {
        return '';
      }
      const selectedAdGroupOptions = adGroupOptions.filter((f) => f.checked);
      if (!(selectedAdGroupOptions.length === adGroupOptions.length)) {
        const queryStrArray = [];
        selectedAdGroupOptions.forEach((adGroup) => {
          let groupData = [];
          if (Array.isArray(adGroup.liData) && adGroup.liData.length > 0) {
            groupData = adGroup.liData;
          } else if (Array.isArray(adGroup.adreadyIds)) {
            groupData = adGroup.adreadyIds;
          }
          if (groupData && groupData.length) {
            groupData.forEach((lid) => {
              const startDate = lid.flightStartDate || lid.startDate;
              if (lid.adreadyId && startDate) {
                queryStrArray.push(`${lid.adreadyId}|${startDate}`);
              }
            });
          }
        });
        return queryStrArray.join(',');
      }
      return '';
    },
    setSelectedFiltersAdG(name, values, isMultiSelectFilter = false, applyFilter = false) {
      if (this.isRestoringSession) return;

      if (!this.isInitialAdGChange) {
        this.handleFilterChangeOnExistingTemplate();
      }
      if (!applyFilter) {
        this.isInitialAdGChange = false;
      }

      if (isMultiSelectFilter) {
        if (name === 'adGroupOptions') {
          if (applyFilter) {
            this.setFilterOptions(name, values);
          }
          // Fix: @selected-options emits ALL options (not just selected), so calling setFilterOptions
          // here would mark every adGroup checked=true, making the run API receive null adGroups.
          // @input already set filters.adGroupOptions correctly — skip here to preserve that.
        } else {
          this.setFilterOptions(name, values);
        }
      } else {
        this.selectedFilters[name] = values.map((val) => {
          val.checked = true;
          return val;
        });
        const formattedOption = this.filters[name].map((opt) => {
          opt.checked = values.some((val) => val.value === opt.value);
          return opt;
        });
        this.setFilterOptions(name, formattedOption);
      }
    },
    setSelectedFilters(name, values, isMultiSelectFilter = false) {
      if (this.isRestoringSession) return;

      if (!this.isInitialCampaignChange) {
        this.handleFilterChangeOnExistingTemplate();
      }
      if (isMultiSelectFilter) {
        this.setFilterOptions(name, values);
      } else {
        this.selectedFilters[name] = values.map((val) => {
          val.checked = true;
          return val;
        });
        const formattedOption = this.filters[name].map((opt) => {
          opt.checked = values.some((val) => val.value === opt.value);
          return opt;
        });
        if (!this.isInitialCampaignChange) {
          if (this.triggerFilterChangeOnCampaign) {
            EventBus.isAdvanceFilterLoaded = false;
            this.advanceFilterLoading = true;
            EventBus.$emit('call-get-advance-filter-data', name, values);
          } else {
            this.triggerFilterChangeOnCampaign = true;
          }
        }
        this.setFilterOptions(name, formattedOption);
      }
      this.isInitialCampaignChange = false;
    },
    onAdvanceFilterLoadingDone(val) {
      this.advanceFilterLoading = !val;
    },
    setFilterOptions(name, values) {
      this.filters[name] = values;
      if (name === 'campaignOptions') {
        this.$store.set('dashboard/filters@campaignOptions', values);
      }
      if (name === 'adGroupOptions') {
        // Fix: without this, changing campaign loaded new adGroups via API but the main screen
        // dropdown still showed the old list (computed adGroupOptions reads from Vuex, not filters directly).
        this.$store.set('dashboard/filters@adGroupOptions', values);
      }
    },
    handleDimensionChange(opt) {
      this.$nextTick(() => {
        this.handleFilterChangeOnExistingTemplate();

        if (this.singleSelectModeDimension) {
          this.selectedDimensionsOptions = [opt];
          this.selectedReportKind = opt;
          this.setMetricOpts();
          return;
        }
        // Check if "Overall" is included in selectedDimensionsOptions
        const overallIndex = this.selectedDimensionsOptions.indexOf('overall');

        if (overallIndex === -1) {
          this.selectedReportKind = this.selectedDimensionsOptions.includes('publisher')
            ? 'publisher'
            : '';
          this.setMetricOpts();
          return;
        }

        if (overallIndex === this.selectedDimensionsOptions.length - 1) {
          this.selectedDimensionsOptions = ['overall'];
          this.selectedReportKind = opt;
          this.setMetricOpts();
          this.selectAllMetrics = false;
        } else if (overallIndex === 0) {
          this.selectedDimensionsOptions.splice(overallIndex, 1);
          this.selectedReportKind = opt;
          this.setMetricOpts();
        }
      });
    },
    toggleSelectAllMetrics() {
      this.handleFilterChangeOnExistingTemplate();

      if (this.selectAllMetrics) {
        this.disabledAllEvents = false;
        this.setMetricOptsForEvents(this.reportMetricsOptions);
        this.selectedMetricsOptions = this.reportMetricsOptions
          .map((option) => option.key)
          .filter((key) => key !== 'ep');
        this.singleSelectModeDimension = false;
        this.reportDimensionsOptions = this.reportDimensionsOptions.filter(
          (dimension) => !['day', 'daypart'].includes(dimension.id)
        );
        this.selectedDimensionsOptions = this.selectedDimensionsOptions.filter(
          (dimension) => !['day', 'daypart'].includes(dimension)
        );
      } else {
        const epIndex = this.reportMetricsOptions.findIndex((option) => option.key === 'ep');
        if (epIndex !== -1 && epIndex + 1 < this.reportMetricsOptions.length) {
          this.selectedMetricsOptions = [this.reportMetricsOptions[epIndex].key];
          this.handleMetricsChange('ep');
        } else {
          this.selectedMetricsOptions = [];
        }
      }
    },
    initializeMetricsSelection() {
      this.$nextTick(() => {
        this.selectAllMetrics = true;
        this.selectedMetricsOptions = this.reportMetricsOptions
          .map((option) => option.key)
          .filter((key) => key !== 'ep');
      });

      this.resetEventSelection();
    },
    handleMetricsChange(selected) {
      this.$nextTick(() => {
        if (this.disabledAllEvents) {
          this.resetEventSelection();
          this.disabledAllEvents = false;
        }

        this.handleFilterChangeOnExistingTemplate();

        if (selected === 'ep') {
          if (this.selectedMetricsOptions.length === 0) {
            this.selectAllMetrics = true;
            this.toggleSelectAllMetrics();
            this.resetEventSelection();
            this.disabledAllEvents = false;
            this.singleSelectModeDimension = false;
            this.reportDimensionsOptions = this.reportDimensionsOptions.filter(
              (dimension) => !['day', 'daypart'].includes(dimension.id)
            );
            this.selectedDimensionsOptions = this.selectedDimensionsOptions.filter(
              (dimension) => !['day', 'daypart'].includes(dimension)
            );
          } else {
            this.selectedMetricsOptions = ['ep'];
            this.selectAllMetrics = false;
            this.eventOptions.forEach((event) => {
              event.selected = false;
              if (event.children && event.children.length) {
                event.children.forEach((subCategory) => {
                  subCategory.selected = false;
                });
              }
            });
            this.disabledAllEvents = true;
            this.singleSelectModeDimension = true;
            this.filters.interval = 'cumulative';
            const dimension = this.reportDimensionsOptions;
            if (!dimension.some((item) => item.id === 'day')) {
              dimension.push({ id: 'day', name: 'Day', type: 'performance' });
            }
            if (!dimension.some((item) => item.id === 'daypart')) {
              dimension.push({ id: 'daypart', name: 'Daypart (EST)', type: 'performance' });
            }
            this.reportDimensionsOptions = dimension;
            this.selectedDimensionsOptions =
              this.selectedDimensionsOptions.length > 1
                ? [this.selectedDimensionsOptions[this.selectedDimensionsOptions.length - 1]]
                : this.selectedDimensionsOptions;
          }
          this.setMetricOptsForEvents(this.reportMetricsOptions);
          return;
        }

        if (this.selectedMetricsOptions.includes('ep')) {
          this.selectedMetricsOptions = this.selectedMetricsOptions.filter(
            (option) => option !== 'ep'
          );
          this.singleSelectModeDimension = false;
          this.reportDimensionsOptions = this.reportDimensionsOptions.filter(
            (dimension) => !['day', 'daypart'].includes(dimension.id)
          );
          this.selectedDimensionsOptions = this.selectedDimensionsOptions.filter(
            (dimension) => !['day', 'daypart'].includes(dimension)
          );
        }

        const allExceptEPSelected = this.reportMetricsOptions
          .filter((option) => option.key !== 'ep')
          .every((option) => this.selectedMetricsOptions.includes(option.key));

        this.selectAllMetrics = allExceptEPSelected;

        if (
          !this.selectedMetricsOptions.includes('ep') &&
          this.selectedMetricsOptions.length === 0
        ) {
          this.selectAllMetrics = true;
          this.toggleSelectAllMetrics();
        }

        // Ensure at least one item is selected
        if (this.selectedMetricsOptions.length === 0) {
          const epIndex = this.reportMetricsOptions.findIndex((option) => option.key === 'ep');
          if (epIndex !== -1 && epIndex + 1 < this.reportMetricsOptions.length) {
            this.selectedMetricsOptions = [this.reportMetricsOptions[epIndex + 1].key];
          }
        }
        this.setMetricOptsForEvents(this.reportMetricsOptions);
      });
    },
    toggleMainCategory(index) {
      this.handleFilterChangeOnExistingTemplate();

      if (this.singleSelectMode) {
        this.eventOptions.forEach((event, i) => {
          if (i !== index) {
            event.selected = false;
          }
        });
      }
      const mainEvent = this.eventOptions[index];

      if (mainEvent.selected) {
        mainEvent.children?.forEach((subCategory) => {
          subCategory.selected = true;
        });
      }

      this.setMetricOptsForEvents(this.reportMetricsOptions);
      const allExceptEPSelected = this.reportMetricsOptions
        .filter((option) => option.key !== 'ep')
        .every((option) => this.selectedMetricsOptions.includes(option.key));

      this.selectAllMetrics = allExceptEPSelected;
    },
    resetSubCategories() {
      this.handleFilterChangeOnExistingTemplate();
    },
    onlyNumber($event) {
      const { key } = $event;

      // Allow numbers (0-9), Backspace, Delete, and control keys, but block the decimal (.)
      if (
        !/^[0-9]$/.test(key) &&
        key !== 'Backspace' &&
        key !== 'Delete' &&
        key !== 'Tab' &&
        key !== 'ArrowLeft' &&
        key !== 'ArrowRight'
      ) {
        $event.preventDefault();
      }
    },
    updateConversionWindowUnit(val) {
      this.handleFilterChangeOnExistingTemplate();
      this.isInitialConversionUnitChange = false;

      if (this.filters.conversionWindowUnit === val.name.toUpperCase()) {
        return;
      }

      if (val.name.toUpperCase() === 'DAYS') {
        this.filters.conversionWindowDisplay = Math.max(
          1,
          Math.round(this.filters.conversionWindowDisplay / 24) // Round the value after division
        );
      } else {
        this.filters.conversionWindowDisplay = Math.round(
          this.filters.conversionWindowDisplay * 24
        ); // Round the value after multiplication
      }
      this.filters.conversionWindowUnit = val.name.toUpperCase();
      this.validateConversionWindow();
    },
    validateConversionWindow() {
      if (!this.isInitialConversionWindowChange) {
        this.handleFilterChangeOnExistingTemplate();
      }
      this.isInitialConversionWindowChange = false;

      const maxValue = this.filters.conversionWindowUnit === 'DAYS' ? 90 : 2160;
      const value = parseFloat(this.filters.conversionWindowDisplay);

      if (value > maxValue) {
        this.filters.conversionWindowDisplay = maxValue;
      }
    },
    handleBlur() {
      const value = parseFloat(this.filters.conversionWindowDisplay);
      if (Number.isNaN(value) || value <= 0) {
        this.filters.conversionWindowDisplay = 1;
      }
    },
    handlePaste(event) {
      event.preventDefault();
      // Prevent the default paste behavior

      // Get the pasted text
      const pasteText = event.clipboardData.getData('text');

      // Sanitize the pasted text (remove anything that isn't a number)
      const sanitizedText = pasteText.replace(/[^0-9]/g, '');

      // Convert the current value to a string (in case it's a number)
      const currentValue = String(this.filters.conversionWindowDisplay);

      // Insert the sanitized text back into the input field
      const start = event.target.selectionStart;
      const end = event.target.selectionEnd;
      const newValue = currentValue.slice(0, start) + sanitizedText + currentValue.slice(end);

      // Update the value
      this.filters.conversionWindowDisplay = newValue;

      // Call the validation method after updating the value
      this.validateConversionWindow();
    },
    async fetchUserSavedReports(advertiserId) {
      try {
        const data = await appConsoleDataLayer.getUserSavedReports(advertiserId);
        if (data?.length > 0) {
          this.$store.set('dashboard/templates@data', data);
          this.$emit('update-data-loading', false);
        } else {
          this.$store.set('dashboard/templates@data', []);
          this.$emit('update-data-loading', false);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
        this.showErrorMessage(ERROR_MESSAGE_FETCH_TEMPLATE);
        this.$emit('update-data-loading', false);
      }
    },
    updateTemplateOptions(data) {
      const dynamicOptions = this.mapAndSortOptions(data);
      let defaultTemplate = this.$store.copy('dashboard/filters@templateOptions');
      defaultTemplate = this.mapAndSortOptions(defaultTemplate);
      this.filters.templateOptions = [...defaultTemplate, ...dynamicOptions];
    },
    mapAndSortOptions(data) {
      const customOrder = [
        NEW_REPORT,
        ALL_TOUCH_REPORT,
        LAST_TOUCH_REPORT,
        CAT_SUBCAT_REPORT,
        FOOT_TRAFFIC_ATTRIBUTED_VISITS_REPORT,
        FOOT_TRAFFIC_SUMMARY_REPORT,
        AUDIENCE_INSIGHTS_CAMPAIGNS_REPORT,
        AUDIENCE_INSIGHTS_CONVERSION_REPORT,
      ];
      const sortedData = data.sort((a, b) => {
        const indexA = customOrder.indexOf(a.id);
        const indexB = customOrder.indexOf(b.id);

        // Handle items explicitly in customOrder
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB; // Sort by custom order
        }

        // Handle items not in customOrder ("wildcard")
        if (indexA === -1 && indexB !== -1) {
          return 1; // 'a' (not in customOrder) comes after 'b' (in customOrder)
        }
        if (indexA !== -1 && indexB === -1) {
          return -1; // 'a' (in customOrder) comes before 'b' (not in customOrder)
        }
        // If both are not in customOrder, sort alphabetically by name (or another default criteria)
        return b.id - a.id;
      });
      return sortedData;
    },
    async fetchAdOptionsFromCampaigns(adGroups = null) {
      if (this.isMniAccount) return;

      this.isAdGroupLoading = true;
      this.campaignOptionsChanged = true;
      const data = this.filters.ioOptions;
      let result = '';
      let campaignIds = '';
      result = data.filter((f) => f?.checked);
      let adGroupData = [];
      let response = '';
      if (result !== data) {
        campaignIds =
          result
            ?.map((res) => {
              return res.key;
            })
            ?.filter((fres) => fres)
            ?.join(',') || '';
        if (campaignIds) {
          const advertiserId = this.advertiser ? this.advertiser.id : 0;
          response = await appConsoleDataLayer.fetchAdGroupOptions(
            advertiserId,
            this.activeSolutionId,
            result
          );
          if (response?.length > 0) {
            adGroupData = response;
          }
          if (adGroupData.length > 0) {
            adGroupData = adGroupData.map((agd) => {
              return { ...agd, value: agd?.name || '', checked: true };
            });
          }
          if (!this.isMniAccount) {
            this.isInitialAdGChange = true;
            if (adGroups) {
              this.isInitialCampaignChange = true;
              this.setFilterOptions('adGroupOptions', adGroupData);
            } else {
              this.setSelectedFiltersAdG('adGroupOptions', adGroupData, true, true);
            }

            if (adGroups) {
              const adGrp =
                this.filters.adGroupOptions || this.getStoredDefaultFilters()?.adGroupOptions;
              const adGrpOpts = this.setSelectedAdGroups(adGrp, adGroups);
              this.filters.adGroupOptions = adGrpOpts;
              this.adGroupsLoaded = true;
            }
          }
        }
        this.isAdGroupLoading = false;
        this.adGroupsLoaded = true;
      }

      if (PRE_DEFINED_TEMPLATES_WITH_NEW.includes(this.filters.template)) {
        // only call for pre defined + new template
        await this.onSelectIO(result);
      }
    },
    async onSelectIO(result) {
      this.isAdGroupLoading = true;
      if (result?.length === 1 && !isBlank(result[0].campaignId) && result[0].campaignId > 0) {
        await this.singleCampaignSettings(result[0].campaignId);
      } else {
        this.selectedCampaignGoal = {};
        this.defaultCampaignSettings();
      }
      this.isAdGroupLoading = false;
    },
    setSelectedAdGroups(adGroupOptions, dbData) {
      // Create a Set from dbData for faster lookups (O(1) average time complexity)
      const dbDataSet = new Set(dbData);

      // Iterate through each ad group option
      adGroupOptions.forEach((adGroup) => {
        adGroup.checked = false;
        const dataItems = adGroup?.liData !== undefined ? adGroup.liData : adGroup.adreadyIds;

        // Check if dataItems exists and is an array
        if (Array.isArray(dataItems)) {
          adGroup.checked = dataItems.some((item) => {
            if (item.adreadyId && (item.startDate || item.flightStartDate)) {
              const date = item.startDate || item.flightStartDate;
              const checkString = `${item.adreadyId}|${date}`;
              return dbDataSet.has(checkString);
            }
            return false;
          });
        }
      });
      return adGroupOptions;
    },

    updateTotalOrUniqueRadioOptions(payload) {
      this.handleFilterChangeOnExistingTemplate();

      this.filters.totalOrUniqueRadioOptions = payload;

      const showUniq =
        this.filters?.totalOrUniqueRadioOptions[1].active &&
        !this.filters?.totalOrUniqueRadioOptions[1].disabled;

      this.$store.set('dashboard/filters@showUniques', showUniq);
      this.filters.showUniques = showUniq;
    },
    updateConversionWindowUnitValue(val) {
      this.filters.conversionWindowUnit = val.name.toUpperCase();
    },
    handleMounted() {
      if (!EventBus.isOverviewMountPending) {
        if (!this.eventOptions) {
          this.fetchEvents();
        } else if (this.eventOptions && !this.eventOptions.length) {
          this.fetchEvents();
        }
        this.$store.set('dashboard/filters@template', 'new');
        this.$store.dispatch('dashboard/resetEventOptions');

        this.mniAccount = [MNI_ACCOUNT_ID, MNI_DEMO_ACCOUNT_ID].includes(this.account?.id);
        this.$store.dispatch('common/setIsMniAccount', this.mniAccount);

        const filterOptionsArray = [
          'audienceOptions',
          'creativeOptions',
          'adGroupOptions',
          'mediaTypeOptions',
          'campaignOptions',
          'ioOptions',
          'keywordOptions',
          'publisherOptions',
        ];
        this.selectedFilters = this.getSelectedFilters(this.genFilters(), filterOptionsArray);

        this.setMetricOptsForEvents(this.reportMetricsOptions);

        if (this.isMniAccount) {
          this.checkIfAllNecessaryDataIsAvailable();
        } else {
          this.$store.dispatch('dashboard/resetToDefaultAPFilters');
          this.checkIfAllNecessaryDataIsAvailableForNonMni();
        }
      } else {
        setTimeout(() => {
          this.handleMounted();
        }, 200);
      }
    },
  },
};
</script>

<style scoped>
.grid-container {
  display: grid;
  grid-template-columns: minmax(280px, 24%) minmax(120px, 12%) minmax(180px, 18%) minmax(190px, 19%) 1fr;
  gap: 10px;
  padding-top: 20px;
  font-family: 'Source Sans Pro', sans-serif;
  overflow-x: auto;
}

.grid-item {
  padding: 10px 5px 10px 5px;
  width: 100%;
  min-width: 0;
  overflow: auto;
  /* border: 1px solid #ddd;
    border-radius: 6px;
    background-color: #f9f9f9; */
}

/* First column specific styling to prevent overlap */
.grid-item:first-child {
  min-width: 280px;
}

.section {
  display: block;
  margin-bottom: 8px;
}

.metrics-label {
  margin-bottom: 5px;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.1em;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.filter {
  ::v-deep .dropdown .dropdown-menu {
    li svg {
      font-size: inherit !important;
    }

    .intip4 {
      top: 2px !important;
      margin-left: 5px !important;

      &::after {
        bottom: 200% !important;
        width: 198px !important;
        margin-left: -73px !important;
        font-weight: 400 !important;
        text-align: left !important;
        letter-spacing: 0em !important;
      }
    }
  }

  .filter-ttl {
    margin-bottom: 10px;
    font-size: 13px;
    font-weight: 400;
    line-height: 1.1em;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .filter-group-report > *:not(:first-child) {
    margin-top: 10px;
  }

  .filter-group-report {
    margin-bottom: 10px;
    padding-left: 15px;

    .conv-content {
      position: relative;
      margin-top: 15px;

      .conv-ttl {
        position: absolute;
        top: -80%;
        font-size: 14px;
        color: #707e8a;
      }
    }

    .opt-b1 {
      display: inline-block;
    }

    .opt-b2 {
      display: inline-block;
      width: 8rem;
      margin-left: 10px;
      vertical-align: bottom;
      position: absolute;
      margin-top: 5px;

      & .checkbox-btn {
        height: unset;
      }

      ::v-deep .checkbox-btn {
        span {
          position: fixed;
          margin: 0 30px !important;
          font-size: 12px !important;
        }
      }
    }

    .input-conv {
      width: 70px;
    }

    .flp-input {
      padding: 10px;
      background-color: transparent;
      border-radius: 4px;
    }
  }
}

.pan-content-top {
  /* margin-left: -26px !important; */
  width: 255px !important;
}

.aggregation {
  padding-top: 5px;
}

::v-deep .media-settings {
  .filter-group-report > *:not(:first-child) {
    margin-top: 12px !important;
  }

  .dropdown {
    width: 200px !important;
    min-width: 180px !important;
    margin: 0 0 0 0 !important;
  }
}

::v-deep .filter.media-settings .dropdown .dropdown-menu {
  max-height: 300px !important;
  overflow: hidden !important;
  overflow-y: auto !important;

  li svg {
    font-size: 17px !important;
    box-shadow: inset 0px 0px 1px 1px #454546;
    color: #fff;
  }
}

.advance-filter-clear-icon {
  position: relative;
  top: -2px;
  left: 5px;
  cursor: pointer;
  font-size: 13px;

  &:hover {
    color: var(--primarycolor) !important;
  }
}

.fp-text-link {
  font-size: 12px;
  color: #707e8a;
  display: inline-block;
  margin-top: 15px;
  display: flex;
  align-items: center;

  & p {
    text-decoration: underline;
    cursor: pointer;
  }

  & p:hover {
    color: var(--primarycolor) !important;
  }

  & span {
    text-decoration: none;
    margin-left: 8px;
    color: #212325 !important;

    .advance-filter-clear-icon:hover {
      color: var(--primarycolor) !important;
    }
  }

  & span:hover {
    color: #cad1d6;
  }
}

::v-deep .method-settings {
  .dropdown {
    width: 198px !important;
    min-width: 198px !important;
  }

  .filter-group-report {
    .tot-uniq,
    .conv-content {
      .conversion-opt-cont {
        display: flex;
      }
    }
  }
}

.dimension-item {
  margin: 5px 0;
}

.dimension-item input[type='checkbox'] {
  margin-right: 10px;
  cursor: pointer;
}

.dimension-item label {
  font-size: 14px;
  color: #333;
  cursor: pointer;
}

h3 {
  margin-bottom: 15px;
  color: #333;
}

.form-group {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.form-group input[type='checkbox'] {
  margin-right: 10px;
}

.schedule-options label,
.form-group label {
  margin-bottom: 5px;
}

input[type='text'],
select {
  width: 100%;
  padding: 8px;
  margin-top: 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.run-report-button {
  background-color: var(--primarycolor);
  color: white;
  border: none;
  padding: 10px 15px;
  font-size: 14px;
  cursor: pointer;
  margin-top: 10px;
  border-radius: 4px;
  width: 100%;
}

.run-report-button:hover {
  background-color: #003366;
}

.drp-wrap {
  font-size: 13px !important;
  padding: 6px 0px 6px 0px !important;
}

.media-select {
  font-size: 13px !important;
}

.filter {
  &.checkbox-select {
    position: relative;
    color: var(--primarydark2);
    max-width: 549px;
    width: 100%;

    max-height: 600px;
    /* Adjust the height */
    overflow-y: auto;
    /* Enable vertical scrolling */
  }

  .checkbox-select::-webkit-scrollbar {
    width: 8px;
  }

  .checkbox-select::-webkit-scrollbar-thumb {
    background-color: #888;
    border-radius: 4px;
  }

  .checkbox-select::-webkit-scrollbar-thumb:hover {
    background-color: #555;
  }

  .checkbox-select__trigger {
    border-radius: 10px;
    background: #fff;
    position: relative;
    z-index: 1;
    height: auto;
    max-height: 40px;
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 8px 10px 8px 15px;
    border: 1px solid #c5d2d8;
    border-radius: 6px;
    transition: all 0.4s ease;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .checkbox-select__check-wrapp {
    margin-bottom: 0px;
    position: relative;

    input[type='checkbox'] {
      display: none;

      & + label {
        position: relative;
        cursor: pointer;
        font-family: 'Manrope', sans-serif;
        font-size: 13px;
        line-height: 22px;
        padding-left: 30px;
        display: inline-block;
        border-radius: 5px;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        -khtml-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
        transition: padding 0.25s ease;

        &:after {
          border: solid 2px var(--primarycolor);
          border-radius: 5px;
          content: '';
          width: 18px;
          height: 18px;
          opacity: 0.7;
          top: 2px;
          left: 0;
          position: absolute;
        }

        &:before {
          width: 14px;
          height: 14px;
          content: '';
          position: absolute;
          top: 0px;
          left: 4px;
          background-color: #fff;
          opacity: 0;
          will-change: transform;
          transform: scale(0.5);
          transition: all 0.2s ease;
        }

        &:hover {
          padding-left: 32px;
        }
      }

      &:disabled {
        color: var(--primarydark);
        opacity: 0.6;
        cursor: auto;
      }

      &:checked {
        & + label {
          &:before {
            margin: 1px 0px 0px 0px;
            opacity: 0.7;
            color: var(--primarycolor);
            font-size: 12px;
            font-family: FontAwesome;
            content: '\f00c';
            transform: scale(1);
          }
        }
      }
    }
  }
}

.checkbox-select__check-wrapp {
  margin-bottom: 10px;
}

.subcategory-container {
  margin-left: 20px;
  /* Indent subcategories */
  padding-top: 5px;
  /* Add some spacing from the parent */
}

.subcategory-item {
  margin-bottom: 8px;
  /* Spacing between subcategories */
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.tooltip-icon {
  margin-left: 5px;
  color: #5a5a5a;
  /* Neutral gray for icon */
  cursor: pointer;
}

.campaign-opts {
  width: 255px !important;
}

::v-deep .campaign-opts .checkbox-select {
  &__dropdown {
    width: 255px !important;
  }
}

.aggregation-type {
  width: 255px !important;
}

::v-deep .aggregation-type .select {
  width: 255px !important;
}

::v-deep .aggregation-type .dropdown-menu {
  width: 255px !important;
}

.media-select {
  width: 255px !important;
}

::v-deep .media-select .select {
  width: 255px !important;
}

::v-deep .media-select .dropdown-menu {
  width: 255px !important;
  overflow: visible !important;
}

.media-select :deep(.intip4[data-tool-tip]::after) {
  bottom: 100%;
  left: 50%;
  margin-bottom: 5px;
  margin-left: 0;
  transform-origin: bottom center;
  transform: translateX(-50%) scale(0);
  width: 155px;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.2em;
  color: #222;
  text-align: center;
  background-color: rgba(245, 245, 250, 1);
  border-radius: 3px;
  transition: transform 0.3s ease;
}

.media-select :deep(.intip4[data-tool-tip]:hover::after) {
  transform: translateX(-50%) scale(1);
  bottom: 100%;
}

.ad-group-select {
  width: 255px !important;
}

::v-deep .ad-group-select .select {
  width: 255px !important;
}

::v-deep .b-select .select {
  width: 255px !important;
}

.b-select {
  width: auto;
  min-width: 200px;
  max-width: 200px;
  margin-right: 20px !important;

  ::v-deep.select {
    margin-top: 0;

    .selected-opt {
      display: block;
      width: 164px !important;
    }
  }
}

::v-deep .dropdown {
  max-width: 200px;
  background-color: #30323a;
  transition: unset !important;

  .select {
    height: 40px;
  }

  .dropdown-menu {
    background-color: #30323a;

    li {
      display: flex;
      padding: 10px 12px !important;
      font-size: 13px !important;

      &:first-child {
        text-transform: capitalize !important;
      }
    }
  }
}

.dropdown-menu .grid-container {
  height: 400px;
  margin-bottom: 35px;
  overflow: hidden;
  -webkit-transition: height 0.3s ease;
  -moz-transition: height 0.3s ease;
  -ms-transition: height 0.3s ease;
  -o-transition: height 0.3s ease;
  transition: height 0.3s ease;
}

.metrics-opts {
  max-width: 450px !important;
}

.message {
  white-space: pre-line;
}

.message-container {
  margin-bottom: 10px;
  padding: 10px;
  text-align: center;
  border-radius: 4px;
}

.message.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.message.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.custom-dimension-tooltip {
  position: absolute;
  top: 40px; /* Position above the input */
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(245, 245, 250, 1); /* White background */
  color: #333; /* Dark text color */
  padding: 8px 12px;
  border-radius: 3px; /* Rounded corners */
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); /* Subtle shadow */
  font-size: 12px;
  white-space: nowrap;
  z-index: 999;
  text-align: center;
}

.custom-dimension-tooltip.show {
  visibility: visible;
  opacity: 1;
  transition: opacity 0.3s ease-in-out; /* Smooth fade-in */
}

.custom-subcat-tooltip {
  position: absolute;
  top: 140px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(245, 245, 250, 1);
  color: #333;
  padding: 8px 12px;
  border-radius: 3px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  font-size: 12px;
  white-space: nowrap;
  z-index: 999;
  text-align: center;
}

.custom-subcat-tooltip.show {
  visibility: visible;
  opacity: 1;
  transition: opacity 0.3s ease-in-out;
}

.custom-interval-tooltip {
  position: absolute;
  top: 370px; /* Position above the input */
  left: 30%;
  transform: translateX(-50%);
  background-color: rgba(245, 245, 250, 1); /* White background */
  color: #333; /* Dark text color */
  padding: 8px 12px;
  border-radius: 3px; /* Rounded corners */
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); /* Subtle shadow */
  font-size: 12px;
  white-space: nowrap;
  z-index: 999;
  text-align: center;
}

.custom-interval-tooltip.show {
  visibility: visible;
  opacity: 1;
  transition: opacity 0.3s ease-in-out; /* Smooth fade-in */
}

.expand-enter-active,
.expand-leave-active {
  -webkit-transition: top 0.3s ease, opacity 0.3s ease;
  -moz-transition: top 0.3s ease, opacity 0.3s ease;
  -ms-transition: top 0.3s ease, opacity 0.3s ease;
  -o-transition: top 0.3s ease, opacity 0.3s ease;
  transition: top 0.3s ease, opacity 0.3s ease;
}

.expand-enter,
.expand-leave-to {
  top: 100% !important;
  opacity: 0 !important;
}

.progress100 {
  width: 100% !important;
}
@media screen {
  .sending-alert {
    position: absolute;
    top: 1%;
    right: -44px;
    width: 250px;
    padding: 16px 30px;
    color: #fff;
    text-align: center;
    background-color: rgba(29, 33, 37, 0.95);
    border-radius: 5px;
    transform: translateX(-20%);
  }
}

.alt-loader {
  position: relative;
  display: block;
  width: 100%;
  height: 4px;
  margin: 11px 0px 2px 0px;
}
.alt-loader-bg {
  position: absolute;
  z-index: 2;
  width: 100%;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.1);
}
.alt-loader-progress {
  position: absolute;
  z-index: 3;
  width: 1%;
  height: 4px;
  background-color: #00abb7;
  -webkit-transition: width 1s ease;
  -moz-transition: width 1s ease;
  -ms-transition: width 1s ease;
  -o-transition: width 1s ease;
  transition: width 1s ease;
}

.error-highlight {
  border: 2px solid var(--primaryred);
  border-radius: 5px;
  transition: border-color 0.3s ease;
  padding: 2px 2px;
}

.error-animation {
  animation: pulse 0.5s ease;
}

.tooltip .tooltiptext {
  visibility: hidden;
  max-width: 200px;
  min-width: 100px;
  width: auto;
  font-size: 12px;
  color: #222;
  pointer-events: none;
  background-color: rgba(245, 245, 250, 0.9);
  text-align: center;
  border-radius: 3px;
  transition: ease 0.3s;
  line-height: 1.2em;
  transform-origin: bottom center;
  padding: 5px;
  position: absolute;
  z-index: 10;
  margin-left: 72px;
  margin-top: -20px;
}
.tooltip:hover .tooltiptext {
  visibility: visible;
}

@keyframes pulse {
  0% {
    border-color: red;
    box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7);
  }
  70% {
    border-color: red;
    box-shadow: 0 0 0 10px rgba(255, 0, 0, 0);
  }
  100% {
    border-color: red;
    box-shadow: 0 0 0 0 rgba(255, 0, 0, 0);
  }
}
</style>

<style>
/* Responsive media queries for grid layout - separate style block to avoid scoped conflicts */
@media screen and (max-width: 1400px) {
  .grid-container {
    grid-template-columns:
      minmax(280px, 1fr) minmax(110px, 1fr) minmax(160px, 1fr) minmax(170px, 1fr)
      minmax(200px, 1fr) !important;
  }
}

@media screen and (max-width: 1200px) {
  .grid-container {
    grid-template-columns:
      minmax(280px, 1fr) minmax(100px, 1fr) minmax(150px, 1fr) minmax(160px, 1fr)
      minmax(180px, 1fr) !important;
  }
}

@media screen and (max-width: 768px) {
  .grid-container {
    grid-template-columns:
      minmax(280px, 1fr) minmax(100px, 1fr) minmax(150px, 1fr) minmax(160px, 1fr)
      minmax(180px, 1fr) !important;
  }
}

@media screen and (max-width: 600px) {
  .grid-container {
    grid-template-columns:
      minmax(280px, 1fr) minmax(100px, 1fr) minmax(150px, 1fr) minmax(160px, 1fr)
      minmax(180px, 1fr) !important;
  }
}
</style>
